import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Map as MapIcon, Shield, RefreshCw, Sparkles, AlertTriangle, Skull, Flame, CloudFog, Snowflake, HelpCircle, Settings, Trophy, Menu, X, Backpack, CheckSquare, Play, User, Globe
} from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import {
  generateMaze, selectDiverseItems, HAZARD_TYPES, HAZARD_ENCOUNTER_IDS,
  LEVEL_CONFIG, ItemType, Direction, Cell, ItemTypeValues, Theme
} from '../utils/mazeUtils';
import { getRandomQuestion, checkAnswer, Question, registry } from '../utils/questionUtils';
import { Capacitor } from '@capacitor/core';
import { crazyGames } from '../utils/crazyGames';
import { revenueCat } from '../utils/revenueCat';
import { admobService } from '../utils/admob';
import { Leaderboard } from './Leaderboard';
import { getHighScores, saveHighScore, isHighScore } from '../utils/leaderboardUtils';
import { PlayerAvatar, AvatarType } from './PlayerAvatar';
import { OperatorPanel } from './OperatorPanel';
import { Paywall } from './Paywall';
import { HouseAd } from './HouseAd';
import { LauncherAd, WEB_LAUNCHER_AD_READY } from './AdBanner';
import { SignupPrompt } from './SignupPrompt';
import { MusicPlayer } from './MusicPlayer';
import {
  LanguageCode, LANGUAGES, loadLanguagePreference, saveLanguagePreference, t, getLanguageInfo
} from '../utils/i18n';
import encounters from '../data/encounters.json';
import {
  logAcademicAnswer,
  logGameOver,
  logHazardResolution,
  logLeaderboardView,
  logLevelEndFailure,
  logLevelEndSuccess,
  logLevelStart,
  logPostScore,
  logRewardEarned,
  logStartAdventure
} from '../analyticsHelper';

// --- CONSTANTS ---
const VISIBILITY_RADIUS = 1;

const ITEMS_CONFIG = {
  [ItemType.Health]: { char: '💊', color: 'text-red-500', name: 'Health Pack' },
  [ItemType.Score]: { char: '💎', color: 'text-cyan-400', name: 'Gem' },
  [ItemType.Hazard]: { char: '🐍', color: 'text-green-600', name: 'Hazard' },
  [ItemType.Weapon]: { char: '🗡️', color: 'text-slate-300', name: 'Dagger' },
  [ItemType.Exit]: { char: '🚪', color: 'text-yellow-500', name: 'Exit' },
  [ItemType.Start]: { char: 'S', color: 'text-white', name: 'Start' },
  [ItemType.Empty]: { char: '', color: '', name: '' },
  [ItemType.Fire]: { char: '🔥', color: 'text-orange-500', name: 'Bushfire' },
  [ItemType.Fumes]: { char: '💨', color: 'text-purple-500', name: 'Toxic Fumes' },
  [ItemType.IceCube]: { char: '🧊', color: 'text-blue-300', name: 'Ice Cube' },
  [ItemType.CO2]: { char: '☁️', color: 'text-gray-400', name: 'CO2 Canister' },
  [ItemType.Antivenom]: { char: '💉', color: 'text-emerald-400', name: 'Antivenom' },
  [ItemType.Robber]: { char: '🦹', color: 'text-purple-600', name: 'Robber' },
  // Specific Hazards
  [ItemType.Snake]: { char: '🐍', color: 'text-green-500', name: 'Snake' },
  [ItemType.Quicksand]: { char: '🏜️', color: 'text-yellow-700', name: 'Quicksand' },
  [ItemType.Bees]: { char: '🐝', color: 'text-yellow-400', name: 'Swarm of Bees' },
  [ItemType.Meteor]: { char: '☄️', color: 'text-orange-600', name: 'Meteor Storm' },
  [ItemType.Aliens]: { char: '👽', color: 'text-green-400', name: 'Violent Alien' },
  [ItemType.Bear]: { char: '🐻', color: 'text-amber-800', name: 'Bear' },
  [ItemType.SolarFlare]: { char: '☀️', color: 'text-yellow-400', name: 'Solar Flare' },
  [ItemType.Earthquake]: { char: '🌍', color: 'text-stone-500', name: 'Earthquake' },
  [ItemType.Dog]: { char: '🐕', color: 'text-amber-600', name: 'Dog Bite' },
  [ItemType.BigCat]: { char: '🐆', color: 'text-yellow-600', name: 'Big Cat' },
  [ItemType.Gorilla]: { char: '🦍', color: 'text-slate-600', name: 'Gorilla' },
  [ItemType.Sunburn]: { char: '🥵', color: 'text-orange-500', name: 'Sunburn' },
  [ItemType.Storm]: { char: '⛈️', color: 'text-blue-500', name: 'Storm' },
  [ItemType.GasLeak]: { char: '🌫️', color: 'text-lime-400', name: 'Gas Leak' },
  [ItemType.FallingIce]: { char: '🌨️', color: 'text-cyan-200', name: 'Falling Ice' },
  [ItemType.Pothole]: { char: '🕳️', color: 'text-stone-700', name: 'Pothole' },
  [ItemType.FastCar]: { char: '🚗', color: 'text-red-500', name: 'Fast Car' },
  // New Items
  [ItemType.Water]: { char: '💧', color: 'text-blue-400', name: 'Water' },
  [ItemType.Bandage]: { char: '🩹', color: 'text-white', name: 'Bandage' },
  [ItemType.Herb]: { char: '🌿', color: 'text-green-400', name: 'Herb' },
  [ItemType.Honey]: { char: '🍯', color: 'text-yellow-400', name: 'Honey' },
  [ItemType.Rope]: { char: '🧶', color: 'text-orange-300', name: 'Rope' },
  [ItemType.Stick]: { char: '🪵', color: 'text-orange-800', name: 'Stick' },
  [ItemType.Teamwork]: { char: '🤝', color: 'text-pink-400', name: 'Teamwork' },
  [ItemType.ShieldGenerator]: { char: '🛡️', color: 'text-cyan-300', name: 'Shield Generator' },
  [ItemType.EvasiveManeuver]: { char: '💨', color: 'text-white', name: 'Evasive Maneuver' },
  [ItemType.NegotiationItem]: { char: '📜', color: 'text-yellow-200', name: 'Treaty' },
  [ItemType.RadiationShield]: { char: '☢️', color: 'text-green-300', name: 'Rad Shield' },
  [ItemType.OxygenTank]: { char: '🤿', color: 'text-blue-500', name: 'Oxygen Tank' },
  [ItemType.OxygenMask]: { char: '😷', color: 'text-white', name: 'Oxygen Mask' },
  [ItemType.Root]: { char: '🥕', color: 'text-orange-600', name: 'Root' },
  [ItemType.Trap]: { char: '🪤', color: 'text-slate-400', name: 'Trap' },
  [ItemType.Shelter]: { char: '⛺', color: 'text-green-700', name: 'Shelter' },
  [ItemType.PharmacyPack]: { char: '🎒', color: 'text-red-400', name: 'Pharmacy Pack' },
  [ItemType.Sunscreen]: { char: '🧴', color: 'text-yellow-100', name: 'Sunscreen' },
  [ItemType.LifeJacket]: { char: '🦺', color: 'text-orange-500', name: 'Life Jacket' },
  [ItemType.Ventilation]: { char: '🌬️', color: 'text-blue-200', name: 'Ventilation' },
  [ItemType.Helmet]: { char: '⛑️', color: 'text-red-600', name: 'Helmet' },
  [ItemType.Shield]: { char: '🛡️', color: 'text-slate-300', name: 'Shield' },
  [ItemType.Money]: { char: '💰', color: 'text-yellow-400', name: 'Money' },
  [ItemType.ReflexBoost]: { char: '⚡', color: 'text-yellow-300', name: 'Reflex Boost' },
  [ItemType.Dodge]: { char: '🏃', color: 'text-white', name: 'Dodge' },
  [ItemType.HealthBerries]: { char: '🍒', color: 'text-red-500', name: 'Health Berries' },
  [ItemType.FlowerPollen]: { char: '🌻', color: 'text-yellow-300', name: 'Flower Pollen' },
  [ItemType.GameMeat]: { char: '🍖', color: 'text-orange-700', name: 'Game Meat' },
  [ItemType.Syringe]: { char: '💉', color: 'text-slate-200', name: 'Syringe' },
  [ItemType.PoisonBerries]: { char: '🫐', color: 'text-purple-800', name: 'Poison Berries' },
  [ItemType.GasMask]: { char: '😷', color: 'text-emerald-600', name: 'Gas Mask' },
};

const SCENERY_IMAGES = [
  "https://images.unsplash.com/photo-1464802686167-b939a6910659?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwxfHxnYWxheHl8ZW58MHx8fHwxNzY2MDAyMjg3fDA&ixlib=rb-4.1.0&q=80&w=1080", // Galaxy
  "https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHw0fHx1bml2ZXJzZXxlbnwwfHx8fDE3NjYwMDIyODd8MA&ixlib=rb-4.1.0&q=80&w=1080", // Universe
  "https://images.unsplash.com/photo-1614732414444-096e5f1122d5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHw4fHxzb2xhciUyMHN5c3RlbXxlbnwwfHx8fDE3NjYwMDIyODd8MA&ixlib=rb-4.1.0&q=80&w=1080", // Solar System
  "https://images.unsplash.com/photo-1648851367298-c83e035dd235?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwzfHxibHVlJTIwc2t5JTIwY2xvdWRzJTIwZGF5fGVufDB8fHx8MTc2NjAwMjI4N3ww&ixlib=rb-4.1.0&q=80&w=1080", // Day Sky
  "https://images.unsplash.com/photo-1502957291543-d85480254bf8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwxfHxuaWdodCUyMHNreSUyMHN0YXJzfGVufDB8fHx8MTc2NjAwMjI4N3ww&ixlib=rb-4.1.0&q=80&w=1080", // Night Sky
  "https://images.unsplash.com/photo-1610044847457-f6aabcbb67d3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwxfHx3YXRlcmZhbGwlMjBuYXR1cmV8ZW58MHx8fHwxNzY2MDAyMjg3fDA&ixlib=rb-4.1.0&q=80&w=1080", // Waterfalls
  "https://images.unsplash.com/photo-1551143404-5672b325d6b2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHw3fHxyaXZlciUyMHNob3JlbGluZXxlbnwwfHx8fDE3NjYwMDIyODd8MA&ixlib=rb-4.1.0&q=80&w=1080", // River Shoreline
  "https://images.unsplash.com/photo-1503755356442-db57841b13f9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHw2fHxvY2VhbiUyMHNob3JlbGluZXxlbnwwfHx8fDE3NjYwMDIyODd8MA&ixlib=rb-4.1.0&q=80&w=1080", // Ocean Shoreline
  "https://images.unsplash.com/photo-1691222691453-b32eba6f451e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwxfHxzZWElMjBzaG9yZWxpbmV8ZW58MHx8fHwxNzY2MDAyMjg3fDA&ixlib=rb-4.1.0&q=80&w=1080", // Sea Shoreline
  "https://images.unsplash.com/photo-1688693155385-53e3662534e0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHw1fHxwb25kJTIwc2hvcmVsaW5lfGVufDB8fHx8MTc2NjAwMjI4N3ww&ixlib=rb-4.1.0&q=80&w=1080"  // Pond Shoreline
];

const THEME_COLORS: Record<Theme, string> = {
  dungeon: 'bg-gradient-to-br from-purple-900 to-slate-800',
  orchard: 'bg-gradient-to-br from-lime-700 to-green-600',
  farm: 'bg-gradient-to-br from-amber-600 to-yellow-700',
  vacation: 'bg-gradient-to-br from-cyan-400 to-blue-600',
  forest: 'bg-gradient-to-br from-emerald-600 to-green-800',
  ocean: 'bg-gradient-to-br from-blue-500 to-cyan-600',
  cartoon: 'bg-gradient-to-br from-pink-500 to-purple-600',
  galaxy: 'bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600',
};

// Wall colours per theme — bright, high-contrast so all levels are as clear as Level 1
const THEME_WALL_COLOR: Record<Theme, string> = {
  dungeon:  '#d946ef', // fuchsia-500 — vivid purple
  orchard:  '#84cc16', // lime-400    — bright green
  farm:     '#fbbf24', // amber-400   — bright gold
  vacation: '#38bdf8', // sky-400     — bright blue
  forest:   '#34d399', // emerald-400 — bright teal
  ocean:    '#60a5fa', // blue-400    — bright blue
  cartoon:  '#f472b6', // pink-400    — bright pink
  galaxy:   '#a78bfa', // violet-400  — bright violet
};

const THEME_IMAGES: Record<Theme, string[]> = {
  dungeon: [],
  orchard: [],
  farm: [],
  vacation: [],
  forest: [],
  ocean: [],
  cartoon: [
    'https://images.unsplash.com/photo-1725621504723-650753ae3994?auto=format&fit=crop&w=1080',
    'https://images.unsplash.com/photo-1759390304300-2acfaf1db11a?auto=format&fit=crop&w=1080',
    'https://images.unsplash.com/photo-1741038273267-82518165fd35?auto=format&fit=crop&w=1080'
  ],
  galaxy: [],
};

// --- ITEM USAGE GUIDE ---
// What each item counters — shown in pickup toast and hint panel
const ITEM_USAGE: Partial<Record<ItemTypeValues, { usage: string; usageColor: string }>> = {
  [ItemType.IceCube]:         { usage: 'Counters: Forest Fire 🔥',                         usageColor: 'text-orange-300' },
  [ItemType.Water]:           { usage: 'Neutralises: Forest Fire 🔥 · Sunburn ☀️',          usageColor: 'text-orange-300' },
  [ItemType.CO2]:             { usage: 'Suppresses: Forest Fire 🔥',                         usageColor: 'text-orange-300' },
  [ItemType.Health]:          { usage: 'General heal +20 HP — works vs most hazards',        usageColor: 'text-green-400' },
  [ItemType.Score]:           { usage: '+100 Score — collected instantly',                   usageColor: 'text-yellow-300' },
  [ItemType.Weapon]:          { usage: 'Counters: Snake 🐍 · Robber 🦹 · Alien 👽 · Bear 🐻', usageColor: 'text-red-400' },
  [ItemType.Antivenom]:       { usage: 'Neutralises: Snake Bite 🐍 (full cure)',             usageColor: 'text-emerald-400' },
  [ItemType.Bandage]:         { usage: 'Treats: Snake Bite 🐍 · Dog Bite 🐕',               usageColor: 'text-pink-300' },
  [ItemType.Herb]:            { usage: 'Soothes: Bee Sting 🐝 · Toxic Fumes 💨',            usageColor: 'text-lime-400' },
  [ItemType.Honey]:           { usage: 'Treats: Bee Sting 🐝 · Toxic Fumes 💨 · Wildlife 🐻 · Leopard 🐆', usageColor: 'text-yellow-400' },
  [ItemType.Rope]:            { usage: 'Escapes: Quicksand 🏜️ · Storm ⛈️ · Earthquake',    usageColor: 'text-amber-400' },
  [ItemType.Stick]:           { usage: 'Helps with: Quicksand 🏜️',                          usageColor: 'text-amber-600' },
  [ItemType.Teamwork]:        { usage: 'Resolves: Quicksand 🏜️ · Alien 👽 · Earthquake',   usageColor: 'text-cyan-400' },
  [ItemType.ShieldGenerator]: { usage: 'Blocks: Meteor Storm ☄️ (full shield)',             usageColor: 'text-violet-400' },
  [ItemType.EvasiveManeuver]: { usage: 'Evades: Meteor Storm ☄️',                           usageColor: 'text-violet-300' },
  [ItemType.LifeJacket]:      { usage: 'Survives: Meteor Storm ☄️ · Storm ⛈️',             usageColor: 'text-blue-400' },
  [ItemType.NegotiationItem]: { usage: 'Pacifies: Violent Alien 👽 (full cure)',             usageColor: 'text-teal-400' },
  [ItemType.RadiationShield]: { usage: 'Blocks: Solar Flare ☀️',                            usageColor: 'text-yellow-500' },
  [ItemType.OxygenTank]:      { usage: 'Counters: Solar Flare ☀️ · Storm ⛈️',              usageColor: 'text-blue-300' },
  [ItemType.OxygenMask]:      { usage: 'Neutralises: Toxic Fumes 💨 · Gas Leak',            usageColor: 'text-emerald-300' },
  [ItemType.Root]:            { usage: 'Treats: Toxic Fumes 💨',                             usageColor: 'text-lime-600' },
  [ItemType.Trap]:            { usage: 'Counters: Hostile Wildlife 🐻',                      usageColor: 'text-amber-500' },
  [ItemType.Shelter]:         { usage: 'Survives: Earthquake 🌍',                            usageColor: 'text-stone-400' },
  [ItemType.PharmacyPack]:    { usage: 'Treats: Dog Bite 🐕',                               usageColor: 'text-red-300' },
  [ItemType.Sunscreen]:       { usage: 'Protects against: Sunburn ☀️',                      usageColor: 'text-yellow-300' },
  [ItemType.Ventilation]:     { usage: 'Clears: Gas Leak 🌫️',                              usageColor: 'text-slate-300' },
  [ItemType.Helmet]:          { usage: 'Blocks: Falling Ice 🧊',                             usageColor: 'text-slate-400' },
  [ItemType.Shield]:          { usage: 'Blocks: Falling Ice 🧊 · Robber 🦹',               usageColor: 'text-slate-300' },
  [ItemType.Money]:           { usage: 'Fixes: Pothole 🕳️ hazard',                          usageColor: 'text-yellow-400' },
  [ItemType.ReflexBoost]:     { usage: 'Evades: Fast Car 🚗',                               usageColor: 'text-yellow-300' },
  [ItemType.Dodge]:           { usage: 'Evades: Fast Car 🚗',                               usageColor: 'text-white' },
  [ItemType.HealthBerries]:   { usage: 'Heals: Gorilla Attack 🦍',                          usageColor: 'text-red-400' },
  [ItemType.FlowerPollen]:    { usage: 'Heals: Gorilla Attack 🦍',                          usageColor: 'text-yellow-300' },
  [ItemType.GasMask]:         { usage: 'Neutralises: Toxic Fumes 💨 · Gas Leak 🌫️',        usageColor: 'text-emerald-600' },
  [ItemType.GameMeat]:        { usage: 'Calms: Hostile Wildlife 🐻 encounters',             usageColor: 'text-orange-700' },
  [ItemType.Syringe]:         { usage: 'Emergency: reduces hazard damage',                  usageColor: 'text-slate-300' },
  [ItemType.PoisonBerries]:   { usage: 'Hazard: use Herb, Syringe, or a Health Pack',        usageColor: 'text-purple-300' },
};

const SUPPLY_SHOP: Array<{ item: ItemTypeValues; cost: number }> = [
  { item: ItemType.Water, cost: 2 },
  { item: ItemType.Antivenom, cost: 2 },
  { item: ItemType.Rope, cost: 2 },
  { item: ItemType.GasMask, cost: 2 },
  { item: ItemType.ShieldGenerator, cost: 3 },
  { item: ItemType.Teamwork, cost: 3 },
  { item: ItemType.NegotiationItem, cost: 3 },
];

// --- AUDIO SERVICE ---
let audioCtx: AudioContext | null = null;

const getAudioContext = () => {
  if (!audioCtx) {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContext) {
      audioCtx = new AudioContext();
    }
  }
  return audioCtx;
};

const playAudioService = (type: 'step' | 'bump' | 'pickup' | 'hazard' | 'levelUp' | 'die' | 'correct' | 'wrong' | 'doorbell' | 'churchBell' | 'gong' | 'cheer' | 'crowdCheer' | 'railCrossing' | 'bleep' | 'itemCollected' | 'fanfare', volumeScale: number = 1.0) => {
  const ctx = getAudioContext();
  if (!ctx) return;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const masterGain = ctx.createGain();

  osc.connect(gain);
  gain.connect(masterGain);
  masterGain.connect(ctx.destination);
  
  masterGain.gain.value = volumeScale;

  const now = ctx.currentTime;

  switch (type) {
    case 'step': {
      // Crisp metallic ting/cling — short high-freq sine with fast decay
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1400, now);
      osc.frequency.exponentialRampToValueAtTime(1100, now + 0.06);
      gain.gain.setValueAtTime(0.07 * volumeScale, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      osc.start(now);
      osc.stop(now + 0.08);
      // Tiny harmonic overtone for "metallic" feel
      const stepHarm = ctx.createOscillator();
      const stepHarmGain = ctx.createGain();
      stepHarm.connect(stepHarmGain);
      stepHarmGain.connect(masterGain);
      stepHarm.type = 'sine';
      stepHarm.frequency.setValueAtTime(2800, now);
      stepHarmGain.gain.setValueAtTime(0.025 * volumeScale, now);
      stepHarmGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      stepHarm.start(now);
      stepHarm.stop(now + 0.05);
      break;
    }
    case 'bump':
      osc.type = 'square';
      osc.frequency.setValueAtTime(100, now);
      osc.frequency.linearRampToValueAtTime(80, now + 0.1);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      osc.start(now);
      osc.stop(now + 0.1);
      break;
    case 'pickup':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
      break;
    case 'hazard':
      // Siren: Alternating high/low (Police/Hospital style)
      osc.type = 'triangle';
      const startTime = now;
      
      // First tone (High)
      osc.frequency.setValueAtTime(880, startTime); // A5
      osc.frequency.setValueAtTime(880, startTime + 0.3);
      
      // Second tone (Low)
      osc.frequency.setValueAtTime(659, startTime + 0.3); // E5
      osc.frequency.setValueAtTime(659, startTime + 0.6);
      
      gain.gain.setValueAtTime(0.2, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.55);
      gain.gain.linearRampToValueAtTime(0, startTime + 0.6);
      
      osc.start(startTime);
      osc.stop(startTime + 0.6);
      break;
    case 'levelUp':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.setValueAtTime(600, now + 0.1);
      osc.frequency.setValueAtTime(800, now + 0.2);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.5);
      osc.start(now);
      osc.stop(now + 0.5);
      break;
    case 'die':
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(10, now + 1);
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.linearRampToValueAtTime(0, now + 1);
      osc.start(now);
      osc.stop(now + 1);
      break;
    case 'correct':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.setValueAtTime(800, now + 0.1);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
      break;
    case 'wrong':
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.linearRampToValueAtTime(100, now + 0.3);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
      break;
    case 'doorbell':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, now);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 1);
      osc.start(now);
      osc.stop(now + 1);
      setTimeout(() => {
        const o2 = ctx.createOscillator();
        const g2 = ctx.createGain();
        o2.connect(g2);
        g2.connect(ctx.destination);
        o2.type = 'sine';
        o2.frequency.setValueAtTime(480, now + 0.6);
        g2.gain.setValueAtTime(0.1, now + 0.6);
        g2.gain.exponentialRampToValueAtTime(0.001, now + 2);
        o2.start(now + 0.6);
        o2.stop(now + 2);
      }, 600);
      break;
    case 'churchBell':
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(200, now);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 3);
      osc.start(now);
      osc.stop(now + 3);
      break;
    case 'gong':
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(100, now);
      osc.frequency.exponentialRampToValueAtTime(80, now + 0.1);
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 4);
      osc.start(now);
      osc.stop(now + 4);
      break;
    case 'railCrossing':
      for(let i=0; i<4; i++) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.frequency.setValueAtTime(800, now + i*0.5);
        g.gain.setValueAtTime(0.1, now + i*0.5);
        g.gain.exponentialRampToValueAtTime(0.001, now + i*0.5 + 0.3);
        o.start(now + i*0.5);
        o.stop(now + i*0.5 + 0.3);
      }
      break;
    case 'cheer':
      [440, 554, 659, 880].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.type = 'triangle';
        o.frequency.setValueAtTime(freq, now + i*0.1);
        g.gain.setValueAtTime(0.1, now + i*0.1);
        g.gain.linearRampToValueAtTime(0, now + i*0.1 + 2);
        o.start(now + i*0.1);
        o.stop(now + i*0.1 + 2);
      });
      break;
    case 'crowdCheer': {
      // Happy crowd: high-to-low pitch sweep ("Yaaay!") with layered voices
      // Layer 1: Rising excitement burst then falling — the "Yaaay" sweep
      const crowdFreqs = [
        { startF: 900, endF: 400, startT: 0,    dur: 1.4, vol: 0.09, type: 'triangle' as OscillatorType },
        { startF: 700, endF: 320, startT: 0.05, dur: 1.3, vol: 0.07, type: 'sine'     as OscillatorType },
        { startF: 1100,endF: 500, startT: 0,    dur: 1.2, vol: 0.06, type: 'triangle' as OscillatorType },
        { startF: 550, endF: 250, startT: 0.1,  dur: 1.1, vol: 0.05, type: 'sine'     as OscillatorType },
        { startF: 1400,endF: 650, startT: 0.02, dur: 0.9, vol: 0.04, type: 'sine'     as OscillatorType },
        { startF: 440, endF: 200, startT: 0.15, dur: 1.0, vol: 0.04, type: 'triangle' as OscillatorType },
      ];
      crowdFreqs.forEach(({ startF, endF, startT, dur, vol, type: oscType }) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(masterGain);
        o.type = oscType;
        o.frequency.setValueAtTime(startF, now + startT);
        o.frequency.exponentialRampToValueAtTime(endF, now + startT + dur);
        g.gain.setValueAtTime(0, now + startT);
        g.gain.linearRampToValueAtTime(vol * volumeScale, now + startT + 0.08);
        g.gain.linearRampToValueAtTime(vol * volumeScale, now + startT + dur * 0.6);
        g.gain.linearRampToValueAtTime(0, now + startT + dur);
        o.start(now + startT);
        o.stop(now + startT + dur + 0.05);
      });
      // Quick rising fanfare at the start (ding-ding-ding before the crowd)
      [523, 659, 784, 1047].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(masterGain);
        o.type = 'sine';
        o.frequency.setValueAtTime(freq, now + i * 0.07);
        g.gain.setValueAtTime(0.08 * volumeScale, now + i * 0.07);
        g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.07 + 0.35);
        o.start(now + i * 0.07);
        o.stop(now + i * 0.07 + 0.4);
      });
      break;
    }
    case 'bleep':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200, now);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      osc.start(now);
      osc.stop(now + 0.1);
      break;
    case 'itemCollected':
      // Mixed pleasant sound: Whistle + Bell + Horn
      // Whistle (high frequency)
      const whistle = ctx.createOscillator();
      const whistleGain = ctx.createGain();
      whistle.connect(whistleGain);
      whistleGain.connect(masterGain);
      whistle.type = 'sine';
      whistle.frequency.setValueAtTime(1800, now);
      whistle.frequency.exponentialRampToValueAtTime(2200, now + 0.15);
      whistleGain.gain.setValueAtTime(0.08, now);
      whistleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      whistle.start(now);
      whistle.stop(now + 0.2);

      // Bell tinkle (triangle with shimmer)
      const bell = ctx.createOscillator();
      const bellGain = ctx.createGain();
      bell.connect(bellGain);
      bellGain.connect(masterGain);
      bell.type = 'triangle';
      bell.frequency.setValueAtTime(1200, now + 0.05);
      bellGain.gain.setValueAtTime(0.12, now + 0.05);
      bellGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      bell.start(now + 0.05);
      bell.stop(now + 0.5);

      // High horn (triumphant note)
      const horn = ctx.createOscillator();
      const hornGain = ctx.createGain();
      horn.connect(hornGain);
      hornGain.connect(masterGain);
      horn.type = 'sawtooth';
      horn.frequency.setValueAtTime(440, now + 0.1);
      hornGain.gain.setValueAtTime(0.1, now + 0.1);
      hornGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      horn.start(now + 0.1);
      horn.stop(now + 0.4);
      break;
    case 'fanfare': {
      // Bright, compact victory sting: punchy brass call, sparkle and a quick crowd lift.
      // Short releases keep the cue celebratory instead of soft or drooping.
      const brassBus = ctx.createGain();
      const brassFilter = ctx.createBiquadFilter();
      brassFilter.type = 'bandpass';
      brassFilter.frequency.value = 1250;
      brassFilter.Q.value = 0.65;
      brassBus.connect(brassFilter);
      brassFilter.connect(masterGain);

      const brassNotes = [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5];
      const brassStarts = [0, 0.12, 0.24, 0.39, 0.54, 0.69];
      const brassLengths = [0.16, 0.16, 0.18, 0.18, 0.16, 0.48];
      brassNotes.forEach((freq, i) => {
        const start = now + brassStarts[i];
        const end = start + brassLengths[i];
        const horn = ctx.createOscillator();
        const hornGain = ctx.createGain();
        horn.type = i === brassNotes.length - 1 ? 'sawtooth' : 'square';
        horn.frequency.setValueAtTime(freq * 0.995, start);
        horn.frequency.linearRampToValueAtTime(freq, start + 0.035);
        horn.connect(hornGain);
        hornGain.connect(brassBus);
        hornGain.gain.setValueAtTime(0.001, start);
        hornGain.gain.exponentialRampToValueAtTime(i === brassNotes.length - 1 ? 0.16 : 0.12, start + 0.012);
        hornGain.gain.setValueAtTime(i === brassNotes.length - 1 ? 0.12 : 0.085, Math.max(start + 0.025, end - 0.08));
        hornGain.gain.exponentialRampToValueAtTime(0.001, end);
        horn.start(start);
        horn.stop(end + 0.02);
      });

      // Bell flashes add the sharp, tangy edge at the top of the brass phrase.
      [1046.5, 1318.51, 1567.98, 2093].forEach((freq, i) => {
        const start = now + 0.27 + i * 0.13;
        const bell = ctx.createOscillator();
        const bellGain = ctx.createGain();
        bell.type = 'sine';
        bell.frequency.setValueAtTime(freq, start);
        bell.connect(bellGain);
        bellGain.connect(masterGain);
        bellGain.gain.setValueAtTime(0.095, start);
        bellGain.gain.exponentialRampToValueAtTime(0.001, start + 0.38);
        bell.start(start);
        bell.stop(start + 0.4);
      });

      // A very short filtered noise swell suggests a crowd cheer without using a sample.
      const cheerLength = Math.floor(ctx.sampleRate * 0.72);
      const cheerBuffer = ctx.createBuffer(1, cheerLength, ctx.sampleRate);
      const cheerData = cheerBuffer.getChannelData(0);
      for (let i = 0; i < cheerLength; i += 1) {
        const progress = i / cheerLength;
        const envelope = Math.sin(Math.PI * progress) * (1 - progress * 0.35);
        cheerData[i] = (Math.random() * 2 - 1) * envelope;
      }
      const cheer = ctx.createBufferSource();
      const cheerFilter = ctx.createBiquadFilter();
      const cheerGain = ctx.createGain();
      cheer.buffer = cheerBuffer;
      cheerFilter.type = 'bandpass';
      cheerFilter.frequency.setValueAtTime(900, now + 0.28);
      cheerFilter.frequency.linearRampToValueAtTime(1800, now + 0.85);
      cheerFilter.Q.value = 0.8;
      cheerGain.gain.setValueAtTime(0.001, now + 0.28);
      cheerGain.gain.linearRampToValueAtTime(0.075, now + 0.48);
      cheerGain.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
      cheer.connect(cheerFilter);
      cheerFilter.connect(cheerGain);
      cheerGain.connect(masterGain);
      cheer.start(now + 0.28);
      break;
    }
  }
};

const CURRENCY_IMAGES = [
   "https://images.unsplash.com/photo-1764865987459-b4d7f9f1e16d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
  "https://images.unsplash.com/photo-1764865988356-45369f310813?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
  "https://images.unsplash.com/photo-1579287953053-7bded5d8adb0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
  "https://images.unsplash.com/photo-1570787019255-5f4e02414dec?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080"
];

// --- SHARE BUTTONS COMPONENT ---
const SHARE_MESSAGES_WIN = [
  // Celebratory
  "Share the Victory!",
  "Spread the Genius!",
  "Challenge a Friend!",
  "Who's Next?",
  // Educational
  "Boost an IQ!",
  "Share the Knowledge!",
  "Train a Brain!",
  "Educate & Elevate!",
  // Competitive
  "Test Your Boss!",
  "Challenge Your Teacher!",
  // Fun/Casual
  "Share the Fun!",
  "Share the Maze!",
  "Spread the Adventure!",
  "Pass the Challenge!"
];

const SHARE_MESSAGES_LOSE = [
  // Competitive
  "Can You Do Better?",
  "Is Your Friend Smarter?",
  "Dare a Colleague!",
  "Challenge Accepted?",
  "Beat My Score!",
  "Test Your Boss!",
  "Challenge Your Teacher!",
  // Fun/Casual
  "Share the Maze!",
  "Pass the Challenge!",
  "Spread the Adventure!"
];

const ShareButtons = ({ score, level, isWin }: { score: number; level: number; isWin: boolean }) => {
  const [copied, setCopied] = React.useState(false);
  const messages = isWin ? SHARE_MESSAGES_WIN : SHARE_MESSAGES_LOSE;
  const randomMessage = messages[Math.floor(Math.random() * messages.length)];

  const gameUrl = "https://nebula-maze.web.app";
  const shareText = isWin
    ? `I scored ${score.toLocaleString()} on Level ${level} of Nebula Maze Daily MazeTest! Can you beat me?`
    : `I scored ${score.toLocaleString()} on Nebula Maze Daily MazeTest! Think you can do better?`;

  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(gameUrl);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`${shareText} ${gameUrl}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-700">
      <p className="text-cyan-400 font-bold text-sm mb-3 text-center">{randomMessage}</p>
      <div className="flex justify-center gap-2 flex-wrap">
        {/* Facebook */}
        <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"
          className="w-10 h-10 bg-blue-600 hover:bg-blue-500 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        </a>
        {/* X/Twitter */}
        <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer"
          className="w-10 h-10 bg-black hover:bg-gray-800 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        </a>
        {/* LinkedIn */}
        <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer"
          className="w-10 h-10 bg-blue-700 hover:bg-blue-600 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        </a>
        {/* WhatsApp */}
        <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer"
          className="w-10 h-10 bg-green-500 hover:bg-green-400 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </a>
        {/* Telegram */}
        <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer"
          className="w-10 h-10 bg-sky-500 hover:bg-sky-400 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
        </a>
        {/* Copy Link */}
        <button onClick={copyToClipboard}
          className={`w-10 h-10 ${copied ? 'bg-green-600' : 'bg-slate-600 hover:bg-slate-500'} rounded-full flex items-center justify-center transition-transform hover:scale-110`}>
          {copied ? (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          ) : (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
          )}
        </button>
      </div>
      {copied && <p className="text-green-400 text-xs text-center mt-2">Link copied!</p>}
    </div>
  );
};

// Landing page share messages
const LANDING_SHARE_MESSAGES = [
  "Share the Adventure!",
  "Challenge a Friend!",
  "Spread the Knowledge!",
  "Test Your Friends!",
  "Share the Fun!",
  "Educate & Elevate!"
];

// Share buttons for landing page (no score)
const LandingShareButtons = () => {
  const [copied, setCopied] = React.useState(false);
  const randomMessage = LANDING_SHARE_MESSAGES[Math.floor(Math.random() * LANDING_SHARE_MESSAGES.length)];

  const gameUrl = "https://nebula-maze.web.app";
  const shareText = "Challenge your brain with Nebula Maze - Daily MazeTest! Educational trivia meets maze adventure.";

  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(gameUrl);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`${shareText} ${gameUrl}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white/50 p-1 md:p-1.5 rounded-xl border border-white/50 shadow-sm w-full">
      <p className="text-slate-700 font-bold text-[9px] md:text-xs mb-0.5 text-center">{randomMessage}</p>
      <div className="flex justify-center gap-0.5 md:gap-1 flex-wrap">
        {/* Facebook */}
        <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"
          className="w-6 h-6 md:w-7 md:h-7 bg-blue-600 hover:bg-blue-500 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        </a>
        {/* X/Twitter */}
        <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer"
          className="w-6 h-6 md:w-7 md:h-7 bg-black hover:bg-gray-800 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        </a>
        {/* LinkedIn */}
        <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer"
          className="w-6 h-6 md:w-7 md:h-7 bg-blue-700 hover:bg-blue-600 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        </a>
        {/* WhatsApp */}
        <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer"
          className="w-6 h-6 md:w-7 md:h-7 bg-green-500 hover:bg-green-400 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </a>
        {/* Telegram */}
        <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer"
          className="w-6 h-6 md:w-7 md:h-7 bg-sky-500 hover:bg-sky-400 rounded-full flex items-center justify-center transition-transform hover:scale-110">
          <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
        </a>
        {/* Copy Link */}
        <button onClick={copyToClipboard}
          className={`w-6 h-6 md:w-7 md:h-7 ${copied ? 'bg-green-600' : 'bg-slate-600 hover:bg-slate-500'} rounded-full flex items-center justify-center transition-transform hover:scale-110`}>
          {copied ? (
            <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          ) : (
            <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
          )}
        </button>
      </div>
      {copied && <p className="text-green-500 text-[8px] text-center mt-0.5">Link copied!</p>}
    </div>
  );
};

// PSA Messages for sponsored section
const PSA_MESSAGES = [
  { text: "Drive Now, Drink at Home", icon: "🚗", color: "text-blue-700" },
  { text: "Drugs Are a Bullet in Slow Motion", icon: "💊", color: "text-red-600" },
  { text: "A Proud Partner Creates Half a Parent", icon: "👨‍👩‍👧", color: "text-purple-700" },
  { text: "Keep Families Whole", icon: "🏠", color: "text-green-700" },
  { text: "Post Your Homework Here = Zero Score", icon: "📝", color: "text-orange-600" },
  { text: "Your Mind is Your Greatest Asset", icon: "🧠", color: "text-cyan-700" },
  { text: "Stay Curious, Stay Learning", icon: "📚", color: "text-indigo-700" },
  { text: "Screen Time < Dream Time", icon: "😴", color: "text-slate-700" },
  { text: "Real Friends Don't Let Friends Text & Drive", icon: "📵", color: "text-red-700" },
  { text: "Be the Change You Want to See", icon: "✨", color: "text-amber-600" },
];

// --- LAUNCHER COMPONENT ---
const Launcher = ({
  onStart,
  onReset,
  difficulty,
  setDifficulty,
  playerIcon,
  setPlayerIcon,
  language,
  setLanguage
}: {
  onStart: () => void,
  onReset: () => void,
  difficulty: 'easy' | 'hard' | 'tough_genius',
  setDifficulty: (d: 'easy' | 'hard' | 'tough_genius') => void,
  playerIcon: AvatarType,
  setPlayerIcon: (i: AvatarType) => void,
  language: LanguageCode,
  setLanguage: (lang: LanguageCode) => void
}) => {
  // Dynamic UTC date state
  const [utcDate, setUtcDate] = React.useState('');

  // Modal overlay state
  const [modalPage, setModalPage] = React.useState<'how-to-play' | 'about' | 'tips' | null>(null);

  // Rotating PSA message
  const [psaIndex, setPsaIndex] = React.useState(() => Math.floor(Math.random() * PSA_MESSAGES.length));

  React.useEffect(() => {
    const interval = setInterval(() => {
      setPsaIndex(prev => (prev + 1) % PSA_MESSAGES.length);
    }, 8000); // Change every 8 seconds
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const updateDate = () => {
      const now = new Date();
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

      const dayName = days[now.getUTCDay()];
      const day = now.getUTCDate();
      const month = months[now.getUTCMonth()];
      const year = now.getUTCFullYear();

      // Add ordinal suffix (1st, 2nd, 3rd, etc.)
      const ordinal = (n: number) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };

      setUtcDate(`${dayName} ${ordinal(day)} ${month}, ${year}`);
    };

    updateDate();
    // Update every minute to catch day changes
    const interval = setInterval(updateDate, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="absolute inset-0 z-40 flex flex-col items-center justify-center px-1 py-0.5 md:px-2 md:py-1 animate-in fade-in overflow-hidden"
      style={{
        backgroundColor: "#add8e6" // Light Blue
      }}>
      <div className="max-w-lg w-full space-y-0.5 md:space-y-1.5 text-center">
        {/* Title with integrated date - ultra compact for portrait */}
        <h1 className="text-base md:text-xl font-black text-slate-900 leading-tight">
          {t('title', language)}
        </h1>
        <p className="text-cyan-700 font-black text-[10px] md:text-sm tracking-wide leading-none">{t('subtitle', language)}<span className="text-slate-500 font-normal text-[8px] md:text-[10px] ml-1 hidden md:inline">{utcDate}</span></p>

        {/* Language Selector - ultra compact for portrait */}
        <div className="flex justify-center gap-0.5 flex-wrap py-0.5">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`px-1 py-0.5 md:px-2 md:py-1 rounded text-[9px] md:text-xs font-medium transition-all
                ${language === lang.code
                  ? 'bg-cyan-600 text-white shadow-md'
                  : 'bg-white/60 text-slate-600 hover:bg-white/80'}`}
              title={lang.name}
            >
              {lang.flag}
            </button>
          ))}
        </div>

        <div
          className="bg-white/80 backdrop-blur-sm p-1.5 md:p-3 rounded-xl border border-white/50 space-y-1 md:space-y-2 shadow-xl"
        >
          {/* Difficulty Selector */}
          <div className="grid grid-cols-3 gap-0.5 md:gap-1.5">
            {(['easy', 'hard', 'tough_genius'] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDifficulty(d)}
                className={`py-0.5 md:py-1.5 px-0.5 md:px-1.5 rounded-lg text-[9px] md:text-xs font-bold capitalize transition-all
                  ${difficulty === d
                    ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-900/20'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
              >
                {d === 'easy' ? t('easy', language) : d === 'hard' ? t('hard', language) : t('toughGenius', language)}
              </button>
            ))}
          </div>

          {/* Icon Selector - ultra compact for portrait */}
          <div className="flex items-center justify-center gap-1">
            <label className="text-[8px] md:text-[10px] font-bold text-slate-500 uppercase">{t('explorerIcon', language)}</label>
            <div className="flex justify-center gap-0.5 md:gap-1.5">
              {(['boy', 'girl', 'cat', 'dog', 'car'] as const).map((icon) => (
                <button
                  key={icon}
                  onClick={() => setPlayerIcon(icon)}
                  className={`p-0.5 md:p-1 rounded-lg transition-all border-2
                    ${playerIcon === icon
                      ? 'border-cyan-500 bg-cyan-500/10 scale-110'
                      : 'border-transparent hover:bg-slate-200'}`}
                >
                  <PlayerAvatar type={icon} className="w-5 h-5 md:w-8 md:h-8" />
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={onStart}
            className="w-full py-1.5 md:py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl font-black text-xs md:text-base shadow-xl shadow-cyan-900/30 transition-all transform hover:scale-[1.02] active:scale-[0.98] flex flex-col items-center justify-center gap-0 animate-pulse"
          >
            <div className="flex items-center gap-1.5">
              <Play className="w-3.5 h-3.5 md:w-5 md:h-5 fill-current" />
              <span>{t('startAdventure', language)}</span>
            </div>
            <span className="text-[8px] md:text-[10px] font-normal opacity-80">{t('pressToBegin', language)}</span>
          </button>

          {/* Nav Buttons */}
          <div className="flex justify-center gap-1.5 md:gap-2 flex-wrap">
            {([
              { key: 'how-to-play', label: 'How to Play' },
              { key: 'about', label: 'About' },
              { key: 'tips', label: 'Tips & Tricks' },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setModalPage(key)}
                className="px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-white/70 hover:bg-white text-slate-700 text-[9px] md:text-xs font-semibold border border-slate-300 hover:border-cyan-400 hover:text-cyan-700 transition-all shadow-sm"
              >
                {label}
              </button>
            ))}
          </div>

          {/* How to Play - inline ultra compact for portrait */}
          <div className="bg-white/70 rounded px-1 py-0.5 md:px-3 md:py-1.5 border border-slate-200 flex items-center justify-center gap-1 md:gap-3">
            <p className="text-[8px] md:text-[10px] font-bold text-slate-600 uppercase">{t('howToPlay', language)}</p>
            <div className="flex items-center gap-0.5 text-slate-700">
              <span className="text-xs md:text-base">⬆️</span>
              <span className="text-xs md:text-base">⬇️</span>
              <span className="text-xs md:text-base">⬅️</span>
              <span className="text-xs md:text-base">➡️</span>
            </div>
            <p className="text-[7px] md:text-[9px] text-slate-500 hidden md:block">{t('howToPlayDesc', language)}</p>
          </div>

          <button
            onClick={onReset}
            className="w-full py-0 text-slate-400 hover:text-slate-600 text-[7px] md:text-[9px] font-medium uppercase tracking-widest transition-colors"
          >
            {t('resetProgress', language)}
          </button>
        </div>

        {/* Share Buttons */}
        <LandingShareButtons />

        {/* Commercial/Advert Link Area - ultra compact for portrait */}
        <div className="bg-white/50 p-1 md:p-2 rounded-xl border border-white/50 shadow-sm w-full">
          <div className="text-[8px] md:text-[10px] font-bold text-slate-500 uppercase mb-0.5">{t('sponsored', language)}</div>

          {/* PSA Message - Above Currency */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded px-1.5 py-1 md:px-3 md:py-2 mb-0.5 transition-all duration-500">
            <div className="flex items-center justify-center gap-1 md:gap-2">
              <span className="text-sm md:text-lg">{PSA_MESSAGES[psaIndex].icon}</span>
              <p className="text-[9px] md:text-xs font-bold text-white text-center">
                {PSA_MESSAGES[psaIndex].text}
              </p>
            </div>
          </div>

          {/* Monetized web placement; existing promotion remains the safe fallback. */}
          {WEB_LAUNCHER_AD_READY && !Capacitor.isNativePlatform() ? (
            <LauncherAd className="w-full overflow-hidden rounded bg-white/70" />
          ) : (
            <div className="grid grid-cols-2 gap-0.5 md:gap-1.5">
              {CURRENCY_IMAGES.slice(0, 2).map((img, i) => (
                <div key={i} className="rounded overflow-hidden bg-slate-200 relative group h-12 md:h-auto md:aspect-video" style={{ maxHeight: '80px' }}>
                  <img src={img} alt="Currency" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 bg-white/90 px-1 py-0.5 rounded text-[8px] md:text-[10px] font-bold text-slate-800 shadow-sm">
                      View Offer
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <a
            href="mailto:originatti@gmail.com?subject=Nebula%20Maze%20advertising%20space"
            className="block mt-0.5 text-[8px] md:text-[10px] text-blue-700 hover:underline font-bold uppercase tracking-wide"
          >
            Ad spot available · Static or banner
          </a>
        </div>
      </div>

      {/* Modal Overlay */}
      {modalPage && (
        <div className="absolute inset-0 z-50 flex flex-col" style={{ backgroundColor: '#000000cc' }}>
          <div className="flex items-center justify-between px-4 py-2 bg-slate-900 shrink-0">
            <span className="text-white font-bold text-sm">
              {modalPage === 'how-to-play' ? 'How to Play' : modalPage === 'about' ? 'About' : 'Tips & Tricks'}
            </span>
            <button
              onClick={() => setModalPage(null)}
              className="text-white hover:text-cyan-400 transition-colors p-1 rounded"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <iframe
            src={`/${modalPage}.html`}
            className="flex-1 w-full border-0 bg-white"
            title={modalPage}
          />
        </div>
      )}
    </div>
  );
};

const GAME_VERSION = '1.7.2';

export const MazeGame: React.FC = () => {
  const [sceneryIndex, setSceneryIndex] = useState(0);

  // Cycle scenery images
  useEffect(() => {
    const interval = setInterval(() => {
      setSceneryIndex(prev => (prev + 1) % SCENERY_IMAGES.length);
    }, 30000); // Change every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Cycle yellow accent color
  useEffect(() => {
    const interval = setInterval(() => {
      const root = document.documentElement;
      // Subtle shift between yellow-300 and yellow-400
      const colors = ['#fde047', '#facc15', '#fef08a', '#eab308'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      root.style.setProperty('--cell-bg-color', randomColor);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  // --- STATE ---
  const [grid, setGrid] = useState<Cell[][]>([]);
  const [playerPos, setPlayerPos] = useState({ x: 0, y: 0 });
  const [hp, setHp] = useState(100);
  const [score, setScore] = useState(0);
  const [bag, setBag] = useState<ItemTypeValues[]>([]);
  const [messageLog, setMessageLog] = useState<string[]>(['Welcome to the Nebula Maze!']);
  const [level, setLevel] = useState(1);
  const [theme, setTheme] = useState<Theme>('dungeon');
  const [modal, setModal] = useState<{
    isOpen: boolean;
    type: 'alert' | 'question' | 'gameOver' | 'levelComplete' | 'inventory';
    title: string;
    message?: string;
    question?: Question;
    hazardType?: string;
    onConfirm?: (answer?: any) => void;
  }>({ isOpen: false, type: 'alert', title: '' });
  
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showOperator, setShowOperator] = useState(false);
  const [scoreHistory, setScoreHistory] = useState<{ level: number, score: number }[]>([]);
  const [playerName, setPlayerName] = useState('');
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const [encounteredHazards, setEncounteredHazards] = useState<string[]>([]);
  const [seenQuestions, setSeenQuestions] = useState<string[]>([]);

  // Monetization & Settings State
  const [runCount, setRunCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null); // Stores Nickname now
  const [showPaywall, setShowPaywall] = useState(false);
  const [adWatched, setAdWatched] = useState(false);
  const [pendingLevel, setPendingLevel] = useState<number | null>(null);
  const [showSignup, setShowSignup] = useState(false);
  const [difficulty, setDifficulty] = useState<'easy' | 'hard' | 'tough_genius'>('easy');
  const [playerIcon, setPlayerIcon] = useState<AvatarType>('boy');
  const [textAnswer, setTextAnswer] = useState('');
  const [multiSelection, setMultiSelection] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<string[]>([]);
  const [questionClue, setQuestionClue] = useState<string | null>(null);
  const [showGameControls, setShowGameControls] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [tokens, setTokens] = useState(() => {
    const saved = Number.parseInt(localStorage.getItem('mazeTokens') || '3', 10);
    return Number.isFinite(saved) && saved >= 0 ? saved : 3;
  });
  const [revealedExitLevel, setRevealedExitLevel] = useState<number | null>(null);
  const [correctAnswersTowardToken, setCorrectAnswersTowardToken] = useState(() => {
    const saved = Number.parseInt(localStorage.getItem('correctAnswersTowardToken') || '0', 10);
    return Number.isFinite(saved) && saved >= 0 && saved < 3 ? saved : 0;
  });

  const handleOpenLeaderboard = () => {
    logLeaderboardView(getHighScores().length);
    setShowLeaderboard(true);
  };

  const saveScoreToLeaderboard = (name: string) => {
    saveHighScore(name, score, level, difficulty);
    logPostScore(score, level);
    handleOpenLeaderboard();
  };

  // Item pickup toast
  const [pickupToast, setPickupToast] = useState<{
    char: string; name: string; nameColor: string; usage: string; usageColor: string;
  } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [bagTooltipIndex, setBagTooltipIndex] = useState<number | null>(null);
  const bagTooltipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showHintPanel, setShowHintPanel] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (modal.isOpen && modal.type === 'question' && modal.question) {
      setMultiSelection([]);
      setSortOrder([]);
      setQuestionClue(null);
    }
  }, [modal.isOpen, modal.type, modal.question]);

  useEffect(() => {
    localStorage.setItem('mazeTokens', String(tokens));
  }, [tokens]);

  useEffect(() => {
    localStorage.setItem('correctAnswersTowardToken', String(correctAnswersTowardToken));
  }, [correctAnswersTowardToken]);

  const [landscapeControlsPosition, setLandscapeControlsPosition] = useState<'left' | 'right'>('left');

  useEffect(() => {
    const initServices = async () => {
      crazyGames.init();
      await revenueCat.init();
      await admobService.init();

      // Verify entitlement from server on startup
      const serverPremium = await revenueCat.checkEntitlement();
      if (serverPremium) {
        setIsPremium(true);
        localStorage.setItem('isPremium', 'true');
      } else if (Capacitor.isNativePlatform()) {
        admobService.showBanner();
      }
    };
    initServices();
  }, []);

  // Audio State
  const [musicVolume, setMusicVolume] = useState(0.1);
  const [sfxVolume, setSfxVolume] = useState(0.3);

  const playSound = useCallback((type: Parameters<typeof playAudioService>[0]) => {
    playAudioService(type, sfxVolume);
  }, [sfxVolume]);

  const containerRef = useRef<HTMLDivElement>(null);

  // --- INIT ---
  useEffect(() => {
    const init = async () => {
      await registry.load(language);
    };
    init();

    // Version Check for Reset
    const savedVersion = localStorage.getItem('gameVersion');
    if (savedVersion !== GAME_VERSION) {
      // New version, reset progress to ensure free games work
      localStorage.removeItem('runCount');
      localStorage.removeItem('adWatched');
      // Keep high scores and email if desired, but for now let's keep it simple
      // localStorage.removeItem('isPremium'); // Maybe keep premium?
      // Let's just reset runCount and adWatched to fix the flow issue
      setRunCount(0);
      setAdWatched(false);
      localStorage.setItem('gameVersion', GAME_VERSION);
    } else {
      const savedRunCount = localStorage.getItem('runCount');
      const savedPremium = localStorage.getItem('isPremium');
      const savedEmail = localStorage.getItem('userEmail');
      if (savedRunCount) setRunCount(parseInt(savedRunCount));
      if (savedPremium === 'true') setIsPremium(true);
      if (savedEmail) setUserEmail(savedEmail);
    }
  }, []);

  const checkAndStartLevel = (newLevel: number) => {
    setLevel(newLevel); // Update the level state
    setAdWatched(false); // Reset ad status for the new level
    
    // This is the part that generates the new maze
    // Ensure it sets playerPos back to {x: 0, y: 0} or your startPos!
    startLevel(newLevel); 
  };

  const handleLauncherStart = async () => {
    // Level 1 always starts free — paywall triggers after completing Level 1
    logStartAdventure();
    crazyGames.gameplayStart();
    // Resume Audio Context on user interaction
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume();
    }

    // No ad at startup — first ad shows only after completing level 1

    // Increment and save the run
    setRunCount(c => {
      const newCount = c + 1;
      localStorage.setItem('runCount', newCount.toString());
      return newCount;
    });

    // Reset ad flag for the current level
    if (adWatched) setAdWatched(false);

    startLevel(1);
    setShowLauncher(false);
  };

  // Remove banner ad when user subscribes; show it again on reset
  useEffect(() => {
    if (!admobService.initialized) return;
    if (isPremium) {
      admobService.removeBanner();
    }
  }, [isPremium]);

  const handleReset = () => {
    if (!window.confirm('Reset this game progress, score, inventory, and clue tokens?')) return;
    setRunCount(0);
    localStorage.setItem('runCount', '0');
    setAdWatched(false);
    setLevel(1);
    setHp(100);
    setScore(0);
    setBag([]);
    setSeenQuestions([]);
    setTokens(3);
    setCorrectAnswersTowardToken(0);
    setPendingLevel(null);
    setModal({ isOpen: false, type: 'alert', title: '' });
    setShowPaywall(false);
    setShowLauncher(true);
    setShowGameControls(false);
    setIsPaused(false);
    alert('Game progress reset. Premium status and high scores were preserved.');
  };

  const handlePaywallUnlock = (method: 'ad' | 'sub') => {
    if (method === 'sub') {
      setIsPremium(true);
      localStorage.setItem('isPremium', 'true');
    } else {
      setAdWatched(true);
    }
    setShowPaywall(false);
    // Auto-proceed to the level that was pending (e.g. Level 2 after watching ad)
    if (pendingLevel !== null) {
      const next = pendingLevel;
      setPendingLevel(null);
      setTimeout(() => checkAndStartLevel(next), 100);
    }
  };

  const handleSignup = (email: string) => {
    setUserEmail(email);
    localStorage.setItem('userEmail', email);
    setShowSignup(false);
    if (isNewHighScore) {
      saveScoreToLeaderboard(email);
    }
  };

  const [showLauncher, setShowLauncher] = useState(true);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [language, setLanguage] = useState<LanguageCode>(() => loadLanguagePreference());

  // Save language preference and reload questions when language changes
  useEffect(() => {
    saveLanguagePreference(language);
    registry.setLanguage(language);
  }, [language]);
  
  // --- THEME BACKGROUND ---
  useEffect(() => {
    const images = THEME_IMAGES[theme];
    if (images && images.length > 0) {
      setBackgroundImage(images[Math.floor(Math.random() * images.length)]);
    } else {
      setBackgroundImage(null);
    }
  }, [theme]);

  // --- ALERT SOUND ---
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!isPaused && modal.isOpen && (modal.type === 'question' || modal.type === 'inventory')) {
      const playAlert = () => playSound('hazard');
      playAlert();
      interval = setInterval(playAlert, 500);
    }
    return () => clearInterval(interval);
  }, [isPaused, modal.isOpen, modal.type]);

  const startLevel = (lvl: number, grantStarterItems = true) => {
    logLevelStart(lvl);
    const config = LEVEL_CONFIG[lvl] || LEVEL_CONFIG[5];
    setTheme(config.theme);

    const { grid: newGrid, startPos } = generateMaze(config.w, config.h, config.shape, difficulty, lvl, bag);
    
    setGrid(newGrid);
    setPlayerPos(startPos);
    setLevel(lvl);
    setRevealedExitLevel(null);
    
    if (grantStarterItems) {
      // Prefer supplies the player does not already carry, avoiding stacks of
      // ice, CO2, or any other single remedy across successive levels.
      const starterItems = [
        ItemType.Health, ItemType.Weapon, ItemType.Antivenom, ItemType.IceCube,
        ItemType.CO2, ItemType.Water, ItemType.Rope, ItemType.Shield,
        ItemType.Bandage, ItemType.Herb, ItemType.Honey, ItemType.GasMask,
        ItemType.Shelter, ItemType.Sunscreen, ItemType.Helmet, ItemType.GameMeat,
      ];
      const newItems = selectDiverseItems(starterItems, 3, bag);
      setBag(prev => [...prev, ...newItems].slice(0, 16));
    }

    if (lvl === 1) {
      setHp(100);
      setScore(0);
      setScoreHistory([]);
      setMessageLog(['Welcome to the Nebula Maze!', 'You received 3 starter items. Good luck!']);
    } else {
      addLog(`Level ${lvl} started! +3 Items`);
    }
    
    updateVisibility(newGrid, startPos.x, startPos.y);
    playSound('levelUp');
    
    setScoreHistory(prev => [...prev, { level: lvl, score }]);
  };

  const closeGameplayOverlays = () => {
    setModal({ isOpen: false, type: 'alert', title: '' });
    setShowPaywall(false);
    setPendingLevel(null);
    setShowHintPanel(false);
    setShowLeaderboard(false);
    setShowOperator(false);
    setShowSignup(false);
  };

  const handleRestartLevel = () => {
    closeGameplayOverlays();
    setShowGameControls(false);
    setIsPaused(false);
    startLevel(level, false);
  };

  const handleRestartGame = () => {
    closeGameplayOverlays();
    setBag([]);
    setSeenQuestions([]);
    setShowLauncher(false);
    setShowGameControls(false);
    setIsPaused(false);
    startLevel(1, true);
    crazyGames.gameplayStart();
  };

  const handleQuitToMenu = () => {
    closeGameplayOverlays();
    setShowGameControls(false);
    setIsPaused(false);
    setShowLauncher(true);
    crazyGames.gameplayStop();
  };

  const exchangeScoreForToken = () => {
    if (score < 50) return;
    setScore(value => value - 50);
    setTokens(value => value + 1);
    addLog('Exchanged 50 score for 1 clue token.');
  };

  const buySupplyItem = (item: ItemTypeValues, cost: number) => {
    if (tokens < cost || bag.length >= 16) return;
    setTokens(value => value - cost);
    setBag(items => [...items, item]);
    addLog(`Bought ${ITEMS_CONFIG[item]?.name} for ${cost} tokens.`);
    showPickupToast(item);
  };

  const revealExit = () => {
    if (showLauncher || grid.length === 0 || tokens < 2 || revealedExitLevel === level) return;
    setTokens(value => value - 2);
    setRevealedExitLevel(level);
    addLog(`The Level ${level} exit is now permanently visible. -2 tokens.`);
    setShowGameControls(false);
    setIsPaused(false);
  };

  const updateVisibility = (currentGrid: Cell[][], px: number, py: number) => {
    const newGrid = [...currentGrid];
    const h = newGrid.length;
    const w = newGrid[0].length;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        newGrid[y][x].visible = false;
        const dist = Math.sqrt(Math.pow(px - x, 2) + Math.pow(py - y, 2));
        if (dist <= VISIBILITY_RADIUS) {
          newGrid[y][x].seen = true;
          newGrid[y][x].visible = true;
        }
      }
    }
    setGrid(newGrid);
  };

  const addLog = (msg: string) => {
    setMessageLog(prev => [msg, ...prev].slice(0, 50));
  };

  // --- SMART QUESTION SELECTION ---
  const getTieredQuestion = (currentLevel: number): Question | null => {
    // 1. Determine Target Difficulty based on Level
    let targetDifficulty = 'easy';
    if (currentLevel >= 3) targetDifficulty = 'medium';
    if (currentLevel >= 6) targetDifficulty = 'hard';

    const unseen = registry.getRandomUnseen(seenQuestions, targetDifficulty as Question['difficulty']);
    if (unseen) return unseen;

    // Only begin a new cycle after every loaded question has appeared once.
    setSeenQuestions([]);
    return registry.getRandomUnseen([], targetDifficulty as Question['difficulty']) || registry.getRandom();
  };

  // --- MOVEMENT ---
  const move = useCallback((dx: number, dy: number) => {
    if (modal.isOpen || isPaused || grid.length === 0 || !grid[0]) return;

    const newX = playerPos.x + dx;
    const newY = playerPos.y + dy;

    if (newX < 0 || newX >= grid[0].length || newY < 0 || newY >= grid.length) {
      playSound('bump');
      return;
    }

    const currentCell = grid[playerPos.y]?.[playerPos.x];
    const targetCell = grid[newY]?.[newX];

    if (!currentCell || !targetCell) {
      playSound('bump');
      return;
    }

    // Check Walls
    let blocked = false;
    if (dx === 1 && (currentCell.walls & Direction.E)) blocked = true;
    if (dx === -1 && (currentCell.walls & Direction.W)) blocked = true;
    if (dy === 1 && (currentCell.walls & Direction.S)) blocked = true;
    if (dy === -1 && (currentCell.walls & Direction.N)) blocked = true;

    if (blocked || !targetCell.valid) {
      playSound('bump');
      return;
    }

    // Move
    setPlayerPos({ x: newX, y: newY });
    updateVisibility(grid, newX, newY);
    playSound('step');

    // Academic Prompt Trigger (5% chance on empty cells)
    if (targetCell.item === ItemType.Empty && Math.random() < 0.05) {
      const question = getTieredQuestion(level);
      if (question) {
        setSeenQuestions(prev => [...prev, question.id]);
        setModal({
          isOpen: true,
          type: 'question',
          title: t('academicChallenge', language),
          message: t('solveForPoints', language),
          question: question,
          onConfirm: (answer: string) => handleQuestionAnswer(answer, question)
        });
        playSound('hazard'); 
      }
    }

    // Check Item
    if (targetCell.item !== ItemType.Empty && targetCell.item !== ItemType.Start) {
      handleItem(targetCell.item, newX, newY);
    }
  }, [grid, playerPos, modal.isOpen, isPaused, seenQuestions]);

  const handleItem = (item: ItemTypeValues, x: number, y: number) => {
    // 1. EXIT LOGIC (Fixed for smoother transition)
    if (item === ItemType.Exit) {
      const validCells = grid.flat().filter(c => c.valid);
      const seenCount = validCells.filter(c => c.seen).length;
      
      if (seenCount / validCells.length < 0.8) {
        setModal({
          isOpen: true,
          type: 'alert',
          title: t('exploreMore', language),
          message: t('exploreMoreMsg', language),
          onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
        });
        playSound('bump');
        return;
      }
      
      playSound('fanfare');
      logLevelEndSuccess(level, score);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3500);
      setModal({
        isOpen: true,
        type: 'levelComplete',
        title: t('levelComplete', language),
        message: `${t('levelComplete', language)} ${level} — ${t('score', language)}: ${score}`,
        onConfirm: async () => {
          // Close modal immediately so UI doesn't hang
          setModal(prev => ({ ...prev, isOpen: false }));

          // Gate every level transition for non-premium players. The Paywall
          // chooses house promotions on Firebase web and revenue ads where a
          // supported SDK is available, so no second interstitial is needed.
          if (!isPremium && !adWatched) {
            setPendingLevel(level + 1);
            setShowPaywall(true);
            return;
          }

          setTimeout(() => {
            checkAndStartLevel(level + 1);
          }, 100);
        }
      });
      return;
    }

    // 2. HAZARD LOGIC (Keep your existing one)
    if (item === ItemType.Hazard || HAZARD_TYPES.includes(item)) {
      playSound('hazard');
      setEncounteredHazards(prev => Array.from(new Set([...prev, item])));
      setModal({
        isOpen: true,
        type: 'inventory',
        title: t('hazardEncounter', language),
        message: `${ITEMS_CONFIG[item].name}! ${t('chooseItem', language)}`,
        hazardType: item,
        onConfirm: (selectedItem) => resolveHazard(item, selectedItem, x, y)
      });
      return;
    }

    // 3. PICKUP LOGIC (Fixed: No more disappearing items when full!)
    if (item === ItemType.Score || item === ItemType.Health) {
      // Instant use - clear the tile
      const newGrid = [...grid];
      newGrid[y][x].item = ItemType.Empty;
      setGrid(newGrid);

      if (item === ItemType.Score) {
        setScore(s => s + 100);
        logRewardEarned('gem', 100);
        addLog("Found a Gem! +100 Score");
        playSound('doorbell');
        showPickupToast(item);
      } else {
        setHp(h => Math.min(100, h + 20));
        addLog("Used Health Pack. +20 HP");
        showPickupToast(item);
      }
      playSound('itemCollected');
    } else {
      // Inventory Items
      if (bag.length < 16) {
        const newGrid = [...grid];
        newGrid[y][x].item = ItemType.Empty; // Only remove from floor if we have room
        setGrid(newGrid);

        setBag(prev => [...prev, item]);
        addLog(`Picked up ${ITEMS_CONFIG[item].name}`);
        showPickupToast(item);
        playSound('itemCollected');
      } else {
        // BAG IS FULL: Item stays on the map!
        addLog("Bag is full! Use an item to make room.");
        playSound('bump');
      }
    }
  };

  const resolveHazard = (hazardType: string, usedItem: string | null, x: number, y: number) => {
    let success = false;
    let msg = '';
    let hpLoss = 0;

    // Map hazardType to encounter ID
    let encounterId = HAZARD_ENCOUNTER_IDS[hazardType as ItemTypeValues] || '';
    if (hazardType === ItemType.Hazard) {
       // Random other hazard
       const others = ['snake_bite', 'bee_sting', 'quicksand', 'hostile_wildlife', 'dog_bite', 'leopard_attack', 'gorilla_attack'];
       encounterId = others[Math.floor(Math.random() * others.length)];
    }

    const encounter = encounters.find(e => e.id === encounterId);
    
    if (encounter) {
      // Create a mutable copy of remedies to allow code-based overrides
      const remedies = { ...(encounter.remedies as Record<string, number>) };

      // Dynamic remedies requested
      if (hazardType === ItemType.Fumes) remedies[ItemType.GasMask] = 0;
      if (hazardType === ItemType.Aliens) remedies[ItemType.Teamwork] = 0;

      const basePoints = encounter.base_points;
      
      if (usedItem && remedies[usedItem] !== undefined) {
        // Valid remedy
        const mitigation = remedies[usedItem];
        hpLoss = mitigation; // Usually negative or 0
        success = true;
        msg = mitigation === 0
          ? `${ITEMS_CONFIG[usedItem].name} fully protected you from ${encounter.name}.`
          : mitigation > 0
            ? `${ITEMS_CONFIG[usedItem].name} restored ${mitigation} HP.`
            : `${ITEMS_CONFIG[usedItem].name} reduced the danger. You lost ${Math.abs(mitigation)} HP.`;
      } else {
        // Invalid or no item
        hpLoss = basePoints;
        success = false;
        msg = `Failed! ${encounter.name} hit you for ${Math.abs(basePoints)} HP.`;
        if (usedItem && encounter.invalid_choice_rule === 'apply_full_loss_and_consume_item') {
           msg += ` You also lost your ${ITEMS_CONFIG[usedItem].name}.`;
        }
      }
    } else {
      // Fallback for unknown hazards
      if (usedItem === ItemType.Weapon || usedItem === ItemType.Antivenom) success = true;
      if (success) {
        msg = "You survived!";
      } else {
        hpLoss = -20;
        msg = "Ouch! -20 HP";
      }
    }

    // Calculate new HP value BEFORE setting state
    const newHp = Math.max(0, Math.min(100, hp + hpLoss));
    logHazardResolution(hazardType, hpLoss, newHp > 0, usedItem);

    // Apply Effects
    setHp(newHp);

    // Remove item from bag
    if (usedItem) setBag(prev => {
      const idx = prev.indexOf(usedItem as ItemTypeValues);
      if (idx > -1) {
        const newBag = [...prev];
        newBag.splice(idx, 1);
        return newBag;
      }
      return prev;
    });

    // Clear hazard
    const newGrid = [...grid];
    newGrid[y][x].item = ItemType.Empty;
    setGrid(newGrid);

    const FAILURE_PHRASES = [
      "Better luck next time",
      "Read More Often",
      "Ask a Teacher",
      "Don't give up!",
      "Keep trying!",
      "Knowledge is power"
    ];
    const failureTitle = FAILURE_PHRASES[Math.floor(Math.random() * FAILURE_PHRASES.length)];

    setModal({
      isOpen: true,
      type: 'alert',
      title: success ? 'Result' : failureTitle,
      message: msg,
      onConfirm: () => {
        setModal(prev => ({ ...prev, isOpen: false }));
        if (newHp <= 0) gameOver();
      }
    });
  };

  const gameOver = () => {
    logLevelEndFailure(level, score);
    logGameOver(score, level);
    crazyGames.gameplayStop();
    playSound('die');
    const isHigh = isHighScore(score);
    setIsNewHighScore(isHigh);

    setModal({
      isOpen: true,
      type: 'gameOver',
      title: 'Game Over',
      message: `You perished in the maze. Final Score: ${score}. Don't ever Quit! You CAN Play Again!`,
      onConfirm: () => {
        // Close modal first
        setModal(prev => ({ ...prev, isOpen: false }));

        if (isHigh) {
          if (!userEmail) {
            setShowSignup(true);
          } else {
            saveScoreToLeaderboard(userEmail);
          }
        }

        // Show interstitial on game over for non-premium players who reached level 2+
        if (!isPremium && level >= 2) {
          if (Capacitor.isNativePlatform() && Capacitor.getPlatform() !== 'ios') {
            admobService.showInterstitial().catch(() => {}).then(() => setShowLauncher(true));
          } else {
            if (Capacitor.getPlatform() === 'ios') {
              setShowLauncher(true);
            } else {
              crazyGames.requestAd('midgame').catch(() => {}).then(() => setShowLauncher(true));
            }
          }
        } else {
          setShowLauncher(true);
        }
      }
    });
  };

  // --- KEYBOARD ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent arrow keys from causing text selection/scrolling
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
      }
      if (showLauncher || isPaused) return;
      switch (e.key) {
        case 'ArrowUp': move(0, -1); break;
        case 'ArrowDown': move(0, 1); break;
        case 'ArrowLeft': move(-1, 0); break;
        case 'ArrowRight': move(1, 0); break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPaused, move, showLauncher]);

  // --- PICKUP TOAST HELPER ---
  const showPickupToast = useCallback((item: ItemTypeValues) => {
    const cfg = ITEMS_CONFIG[item];
    const usageInfo = ITEM_USAGE[item];
    if (!cfg) return;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setPickupToast({
      char: cfg.char,
      name: cfg.name,
      nameColor: cfg.color,
      usage: usageInfo?.usage ?? 'A useful survival item',
      usageColor: usageInfo?.usageColor ?? 'text-slate-300',
    });
    toastTimerRef.current = setTimeout(() => setPickupToast(null), 4000);
  }, []);

  // --- RENDER HELPERS ---
  const handleManualMove = (dx: number, dy: number) => {
    if (modal.isOpen || isPaused) return;
    // Dismiss pickup toast on any move
    if (pickupToast) {
      setPickupToast(null);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    }
    move(dx, dy);
  };

  const useQuestionClue = () => {
    const question = modal.question;
    if (!question || tokens < 1 || questionClue) return;

    let clue = '';
    if (question.type === 'sort') {
      clue = `The first item is “${question.answer.split(',')[0].trim()}”.`;
    } else if (question.type === 'multi') {
      clue = `One correct selection is “${question.answer.split(',')[0].trim()}”.`;
    } else if (question.options?.length) {
      const wrong = question.options.find(option => option !== question.answer);
      clue = wrong ? `Eliminate “${wrong}”; it is not the answer.` : `The answer begins with “${question.answer.charAt(0)}”.`;
    } else {
      clue = `The answer begins with “${question.answer.charAt(0)}” and has ${question.answer.length} characters.`;
    }

    setTokens(value => value - 1);
    setQuestionClue(clue);
    addLog('Used 1 clue token.');
  };

  const handleQuestionAnswer = (answer: string | null, question: Question) => {
    if (!answer) {
      // Skip (-5 points)
      logAcademicAnswer(question.id, 'skipped', question.category, -5);
      setScore(s => Math.max(0, s - 5));
      addLog('Skipped question. -5 points.');
      setModal({ ...modal, isOpen: false });
      setTextAnswer('');
      return;
    }

    const isCorrect = checkAnswer(question, answer);
    if (isCorrect) {
      // Correct (+10 to +40 points)
      // Difficulty scaling: Easy=10, Hard=20, Tough=40
      let points = difficulty === 'easy' ? 10 : difficulty === 'hard' ? 20 : 40;
      if (question.category === 'Social') points += 10; // Bonus for social behaviors
      
      const healthGain = 5;
      logAcademicAnswer(question.id, 'correct', question.category, points);
      setScore(s => s + points);
      setHp(h => Math.min(100, h + healthGain));
      const nextTokenProgress = correctAnswersTowardToken + 1;
      if (nextTokenProgress >= 3) {
        setTokens(value => value + 1);
        setCorrectAnswersTowardToken(0);
        addLog(`Correct! +${points} points, +${healthGain} HP, and +1 clue token.`);
      } else {
        setCorrectAnswersTowardToken(nextTokenProgress);
        addLog(`Correct! +${points} points and +${healthGain} HP. Token progress ${nextTokenProgress}/3.`);
      }
      playSound('churchBell');
    } else {
      // Wrong (-10 points)
      logAcademicAnswer(question.id, 'wrong', question.category, -10);
      setScore(s => Math.max(0, s - 10));
      addLog('Wrong answer. -10 points.');
      playSound('gong');
    }
    setModal({ ...modal, isOpen: false });
    setTextAnswer('');
  };

  const renderEncounter = () => {
    if (modal.type === 'inventory') {
      const hazardItem = modal.hazardType as ItemTypeValues;
      const hazardConfig = ITEMS_CONFIG[hazardItem];

      return (
        <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 animate-in zoom-in max-w-lg w-full max-h-[88vh] overflow-y-auto animate-flash-alert">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            {hazardConfig && (
              <span className={`text-5xl ${hazardConfig.color}`}>{hazardConfig.char}</span>
            )}
            <div>
              <h2 className="text-xl font-bold text-white leading-tight">{modal.title}</h2>
              {hazardConfig && (
                <div className="text-cyan-400 font-semibold text-base leading-tight">{hazardConfig.name}</div>
              )}
            </div>
          </div>

          <p className="text-slate-300 mb-4 text-base leading-snug">{modal.message}</p>

          {/* Item tiles: 3-col grid, horizontal layout — emoji left, name right */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {bag.map((item, i) => (
              <button
                key={i}
                onClick={() => modal.onConfirm && modal.onConfirm(item)}
                className="flex items-center gap-2 px-2 py-3 bg-slate-700 hover:bg-slate-600 active:bg-slate-500 rounded-lg transition-colors border border-slate-600 hover:border-cyan-500 text-left"
                title={ITEMS_CONFIG[item]?.name}
              >
                <span className="text-2xl flex-shrink-0">{ITEMS_CONFIG[item]?.char}</span>
                <span className="text-sm text-slate-200 font-medium leading-tight line-clamp-2">{ITEMS_CONFIG[item]?.name}</span>
              </button>
            ))}
          </div>

          <button
            onClick={() => modal.onConfirm && modal.onConfirm(null)}
            className="w-full py-3 bg-red-900/50 hover:bg-red-900/80 text-red-200 rounded-lg text-base font-bold transition-colors border border-red-800"
          >
            {t('takeDamage', language)}
          </button>
        </div>
      );
    }
    
    if (modal.type === 'question' && modal.question) {
      const q = modal.question;
      const isTextType = !q.options || q.options.length === 0;
      const isMulti = q.type === 'multi';
      const isSort = q.type === 'sort';

      return (
        <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 animate-in zoom-in max-w-lg w-full max-h-[88vh] overflow-y-auto animate-flash-alert">
          <div className="flex justify-between items-start mb-3">
            <h2 className="text-xl font-bold text-white">{t('academicChallenge', language)}</h2>
            <span className="text-sm bg-cyan-900 text-cyan-200 px-2 py-0.5 rounded uppercase font-semibold">{q.category}</span>
          </div>

          {q.image && (
            <div className="mb-2 rounded-lg overflow-hidden border border-slate-600 bg-slate-800 flex justify-center items-center p-1.5 min-h-[90px]">
              <img
                src={q.image}
                alt={q.imageDesc || 'Question image'}
                className="max-h-28 max-w-full object-contain rounded"
                onError={(e) => {
                  const img = e.currentTarget;
                  const parent = img.parentElement;
                  if (parent) {
                    img.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'flex flex-col items-center justify-center gap-1 py-2';
                    fallback.innerHTML = `<span style="font-size:2rem">🖼️</span><span style="color:#94a3b8;font-size:0.7rem;text-align:center">${q.imageDesc || 'Image unavailable'}</span>`;
                    parent.appendChild(fallback);
                  }
                }}
              />
            </div>
          )}

          <p className="text-slate-300 mb-4 font-medium text-base">{q.text}</p>

          <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-950/30 p-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-amber-200">🪙 {tokens} clue token{tokens === 1 ? '' : 's'}</span>
              <button
                onClick={useQuestionClue}
                disabled={tokens < 1 || Boolean(questionClue)}
                className="rounded-md bg-amber-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {questionClue ? 'Clue used' : 'Use clue · 1 token'}
              </button>
            </div>
            {questionClue && <p className="mt-2 text-sm font-medium text-amber-100">💡 {questionClue}</p>}
          </div>

          <div className="space-y-2">
            {isMulti ? (
              <>
                <div className="grid grid-cols-1 gap-2">
                  {q.options?.map((opt, i) => {
                    const selected = multiSelection.includes(opt);
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          setMultiSelection(prev => 
                            prev.includes(opt) ? prev.filter(x => x !== opt) : [...prev, opt]
                          );
                        }}
                        className={`w-full p-2 text-left rounded-lg transition-colors border font-medium text-sm flex justify-between items-center
                          ${selected
                            ? 'bg-cyan-900/50 border-cyan-500 text-cyan-100'
                            : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}
                      >
                        {opt}
                        {selected && <span className="text-cyan-400">✓</span>}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => modal.onConfirm && modal.onConfirm(multiSelection.join(','))}
                  className="w-full py-2 mt-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-lg font-bold transition-colors text-sm"
                >
                  Submit Selection
                </button>
              </>
            ) : isSort ? (
              <>
                <div className="text-xs text-slate-400 mb-1">Tap to add to order:</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {q.options?.filter(opt => !sortOrder.includes(opt)).map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setSortOrder(prev => [...prev, opt])}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg text-sm transition-colors"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                
                <div className="text-xs text-slate-400 mb-1">Your Order (Tap to remove):</div>
                <div className="space-y-2 min-h-[100px] bg-slate-900/50 p-2 rounded-lg border border-slate-700">
                  {sortOrder.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setSortOrder(prev => prev.filter((_, idx) => idx !== i))}
                      className="w-full p-2 text-left bg-cyan-900/30 border border-cyan-500/30 rounded text-cyan-100 text-sm flex items-center gap-2"
                    >
                      <span className="bg-cyan-900 text-cyan-400 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold">{i + 1}</span>
                      {opt}
                    </button>
                  ))}
                  {sortOrder.length === 0 && (
                    <div className="text-slate-600 text-xs text-center py-4 italic">Select items above to order them</div>
                  )}
                </div>

                <button
                  onClick={() => modal.onConfirm && modal.onConfirm(sortOrder.join(','))}
                  className="w-full py-2 mt-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-lg font-bold transition-colors text-sm"
                  disabled={sortOrder.length === 0}
                >
                  Submit Order
                </button>
              </>
            ) : !isTextType ? (
              q.options?.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => modal.onConfirm && modal.onConfirm(opt)}
                  className="w-full p-4 text-left bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200 transition-colors border border-slate-600 hover:border-cyan-500 font-medium text-base"
                >
                  {opt}
                </button>
              ))
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={textAnswer}
                  onChange={(e) => setTextAnswer(e.target.value)}
                  placeholder="Type your answer..."
                  className="w-full p-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none text-base"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      modal.onConfirm && modal.onConfirm(textAnswer);
                    }
                  }}
                />
                <button
                  onClick={() => modal.onConfirm && modal.onConfirm(textAnswer)}
                  className="w-full py-3 bg-cyan-700 hover:bg-cyan-600 text-white rounded-lg font-bold transition-colors text-base"
                >
                  Submit Answer
                </button>
              </div>
            )}

            <button
              onClick={() => modal.onConfirm && modal.onConfirm(null)}
              className="w-full py-3 mt-2 text-slate-400 hover:text-slate-200 text-base font-medium transition-colors border-t border-slate-700"
            >
              Skip Question (-5 points)
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
    <style>{`
      .portrait-maze { width: min(96vw, calc(100dvh - 360px)); height: min(96vw, calc(100dvh - 360px)); }
      @media (min-width: 376px) {
        .portrait-maze { width: min(96vw, calc(100dvh - 395px)); height: min(96vw, calc(100dvh - 395px)); }
      }
    `}</style>
    <div className="flex flex-col h-screen bg-slate-950 text-slate-200 font-sans selection:bg-cyan-500/30">
      <button
        onClick={() => { setShowGameControls(true); setIsPaused(true); }}
        className="fixed left-2 top-8 z-[90] flex items-center gap-1.5 rounded-lg border border-cyan-400/60 bg-slate-950/95 px-2.5 py-2 text-xs font-bold text-cyan-200 shadow-xl hover:bg-slate-800"
        aria-label="Pause and open game controls"
      >
        <Menu className="h-4 w-4" />
        {showLauncher ? 'Game menu' : 'Pause'}
      </button>

      {showGameControls && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 p-3 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-cyan-500/50 bg-slate-900 p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-white">Game controls</h2>
                <p className="text-xs text-slate-400">Level {level} · Score {score} · 🪙 {tokens} token{tokens === 1 ? '' : 's'}</p>
              </div>
              <button
                onClick={() => { setShowGameControls(false); setIsPaused(false); }}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Resume game"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => { setShowGameControls(false); setIsPaused(false); }} className="rounded-lg bg-cyan-600 px-3 py-3 font-bold text-white hover:bg-cyan-500">
                ▶ Resume
              </button>
              <button onClick={handleRestartLevel} disabled={showLauncher || grid.length === 0} className="rounded-lg bg-indigo-700 px-3 py-3 font-bold text-white hover:bg-indigo-600 disabled:opacity-40">
                ↻ Restart level
              </button>
              <button onClick={handleRestartGame} className="rounded-lg bg-violet-700 px-3 py-3 font-bold text-white hover:bg-violet-600">
                ⟳ Restart game
              </button>
              <button onClick={handleQuitToMenu} className="rounded-lg bg-slate-700 px-3 py-3 font-bold text-white hover:bg-slate-600">
                ⇥ Quit to menu
              </button>
            </div>

            <div className="mt-5 rounded-xl border border-amber-500/40 bg-amber-950/20 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-amber-200">Clue tokens</h3>
                  <p className="text-xs text-slate-400">Earn one for every 3 correct answers ({correctAnswersTowardToken}/3), or exchange score.</p>
                </div>
                <button onClick={exchangeScoreForToken} disabled={score < 50} className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-bold text-white hover:bg-amber-500 disabled:opacity-40">
                  50 score → 1 token
                </button>
              </div>
              <button
                onClick={revealExit}
                disabled={showLauncher || grid.length === 0 || tokens < 2 || revealedExitLevel === level}
                className="mt-3 w-full rounded-lg border border-yellow-500/50 bg-yellow-500/15 px-3 py-2 text-sm font-bold text-yellow-200 hover:bg-yellow-500/25 disabled:opacity-40"
              >
                {revealedExitLevel === level ? '🚪 Exit revealed for this level' : '🚪 Reveal and keep exit visible · 2 tokens'}
              </button>
            </div>

            <div className="mt-4">
              <div className="mb-2 flex items-end justify-between gap-2">
                <div>
                  <h3 className="font-bold text-white">Survival supply shop</h3>
                  <p className="text-xs text-slate-400">Buy mitigators before the next hazard. Inventory {bag.length}/16.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SUPPLY_SHOP.map(({ item, cost }) => (
                  <button
                    key={item}
                    onClick={() => buySupplyItem(item, cost)}
                    disabled={tokens < cost || bag.length >= 16}
                    className="rounded-lg border border-slate-600 bg-slate-800 p-2 text-left hover:border-cyan-500 hover:bg-slate-700 disabled:opacity-40"
                  >
                    <div className="text-xl">{ITEMS_CONFIG[item]?.char}</div>
                    <div className="text-xs font-bold text-white">{ITEMS_CONFIG[item]?.name}</div>
                    <div className="text-[11px] text-amber-300">{cost} tokens</div>
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleReset} className="mt-5 w-full rounded-lg border border-red-800 bg-red-950/40 px-3 py-2 text-sm font-semibold text-red-200 hover:bg-red-900/60">
              Reset all game progress
            </button>
          </div>
        </div>
      )}

      {/* SCROLLING BANNER */}
      <div className="h-7 bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 border-b border-cyan-500/30 overflow-hidden relative z-30">
        <div className="absolute whitespace-nowrap animate-scroll-left text-white text-xs font-bold tracking-wider flex items-center h-full">
          {/* First copy - extended phrases for continuous scroll */}
          <span className="inline-flex items-center gap-3 px-2">
            <span className="text-cyan-300">NEBULA MAZE</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Fog of War</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Survival</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Trivia</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">IQ Tune-Up</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Game & Learn</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Adventure</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Common Sense</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Productive Gaming</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">NEBULA MAZE</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Fog of War</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Survival</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Trivia</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">IQ Tune-Up</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Game & Learn</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Adventure</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Common Sense</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Productive Gaming</span>
            <span className="text-purple-300">•</span>
          </span>
          {/* Second copy (identical for seamless loop) */}
          <span className="inline-flex items-center gap-3 px-2">
            <span className="text-cyan-300">NEBULA MAZE</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Fog of War</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Survival</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Trivia</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">IQ Tune-Up</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Game & Learn</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Adventure</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Common Sense</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Productive Gaming</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">NEBULA MAZE</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Fog of War</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Survival</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Trivia</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">IQ Tune-Up</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Game & Learn</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Adventure</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Common Sense</span>
            <span className="text-purple-300">•</span>
            <span className="text-cyan-300">Productive Gaming</span>
            <span className="text-purple-300">•</span>
          </span>
        </div>
      </div>

      {/* MERGED STATS PANEL - compact single-row strip (Hidden in landscape) */}
      {!showLauncher && (
        <div className="bg-lime-200 border-b border-lime-300 z-20 flex-shrink-0 landscape:hidden" style={{padding:'3px 4px 3px 4px'}}>
          <div className="flex gap-1 items-center">
            {/* Left: Trophy / Settings / Hint — compact column */}
            <div className="flex flex-col gap-0.5 flex-shrink-0">
              <button onClick={handleOpenLeaderboard} className="w-9 h-9 xs:w-11 xs:h-11 bg-slate-800/90 hover:bg-slate-700 rounded transition-colors border border-slate-600 flex items-center justify-center">
                <Trophy className="w-6 h-6 xs:w-8 xs:h-8 text-yellow-400" />
              </button>
              <button onClick={() => setShowOperator(true)} className="w-9 h-9 xs:w-11 xs:h-11 bg-slate-800/90 hover:bg-slate-700 rounded transition-colors border border-slate-600 flex items-center justify-center">
                <Settings className="w-6 h-6 xs:w-8 xs:h-8 text-cyan-400" />
              </button>
              <button onClick={() => setShowHintPanel(true)} className="w-9 h-9 xs:w-11 xs:h-11 bg-amber-700/80 hover:bg-amber-600 rounded border border-amber-500 flex items-center justify-center">
                <span className="text-xl xs:text-2xl leading-none">💡</span>
              </button>
            </div>

            {/* Level / Score / Health — compact column */}
            <div className="flex flex-col gap-0.5 flex-shrink-0">
              <div className="w-[46px] h-9 xs:w-[58px] xs:h-11 bg-indigo-800/90 rounded border border-indigo-500 flex flex-col items-center justify-center">
                <div className="text-[7px] xs:text-[8px] text-indigo-200 uppercase font-bold leading-none">LEVEL</div>
                <div className="text-[10px] xs:text-xs font-mono font-bold text-cyan-300 leading-none">{level}</div>
              </div>
              <div className="w-[46px] h-9 xs:w-[58px] xs:h-11 bg-green-700/90 rounded border border-green-600 flex flex-col items-center justify-center">
                <div className="text-[7px] xs:text-[8px] text-white uppercase font-bold leading-none">SCR</div>
                <div className="text-[10px] xs:text-xs font-mono font-bold text-yellow-300 leading-none">{score}</div>
              </div>
              <div className="w-[46px] h-9 xs:w-[58px] xs:h-11 bg-slate-800/90 rounded border border-slate-600 flex flex-col items-center justify-center">
                <div className="text-[7px] xs:text-[8px] text-white uppercase font-bold leading-none">HP</div>
                <div className={`text-[10px] xs:text-xs font-mono font-bold ${hp > 50 ? 'text-green-400' : 'text-red-400'} leading-none`}>{hp}%</div>
              </div>
            </div>

            {/* Inventory grid — fills remaining width, 2 rows max shown */}
            <div className="flex-1 min-w-0">
              <div className="text-[8px] font-black text-slate-700 uppercase text-center leading-none mb-0.5">NERD METER</div>
              <div className="grid grid-cols-8 md:grid-cols-[repeat(8,_minmax(34px,_56px))] gap-0.5 w-full relative">
                {Array.from({ length: 16 }).map((_, i) => {
                  const item = bag[i];
                  return (
                    <div
                      key={i}
                      className={`relative h-[34px] md:h-[56px] rounded border ${item ? 'bg-slate-700/90 border-slate-600 cursor-pointer active:scale-95' : 'bg-slate-300/30 border-slate-400/30'} flex flex-col items-center justify-center overflow-hidden`}
                      onClick={() => {
                        if (!item) return;
                        if (bagTooltipTimer.current) clearTimeout(bagTooltipTimer.current);
                        setBagTooltipIndex(i);
                        bagTooltipTimer.current = setTimeout(() => setBagTooltipIndex(null), 2000);
                      }}
                    >
                      {item && <span className="text-2xl leading-none">{ITEMS_CONFIG[item]?.char}</span>}
                      {item && <span className="text-[6px] md:text-[8px] text-slate-300 font-medium leading-none text-center w-full px-0.5 truncate">{ITEMS_CONFIG[item]?.name}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONTENT WRAPPER - Flex Row on Desktop */}
      <div className="flex-1 flex flex-col landscape:flex-row overflow-hidden relative min-h-0">
        
        {/* DESKTOP LEFT AD - REMOVED (Only one ad panel in landscape) */}

        {/* GAME AREA */}
        <div className="flex-1 flex flex-col relative min-h-0">
        {/* LAUNCHER OVERLAY */}
        {showLauncher && (
          <Launcher
            onStart={handleLauncherStart}
            onReset={handleReset}
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            playerIcon={playerIcon}
            setPlayerIcon={setPlayerIcon}
            language={language}
            setLanguage={setLanguage}
          />
        )}

        {/* AD INTERSTITIAL REMOVED */}



           {/* MAZE PANEL WRAPPER */}
         <div className="flex-1 flex flex-col relative min-h-0"
          ref={containerRef}
          style={{
            backgroundImage: `url(${SCENERY_IMAGES[sceneryIndex]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}>

          {/* PORTRAIT MODE - fills all remaining height, maze dominates */}
          <div className="landscape:hidden flex flex-col flex-1 min-h-0 items-center justify-between px-1 pt-1 pb-2 relative">
            {/* Maze Grid - Portrait: fills space between stats strip and controls */}
            <div
              className="portrait-maze relative bg-slate-900/80 backdrop-blur-sm rounded-lg shadow-2xl overflow-hidden border border-slate-800 flex-shrink-0"
              style={{
                aspectRatio: '1 / 1',
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
            {/* Grid Rendering */}
            <div
              className="absolute inset-0 grid"
              style={{
                gridTemplateColumns: `repeat(${grid[0]?.length || 1}, 1fr)`,
                gridTemplateRows: `repeat(${grid.length || 1}, 1fr)`,
              }}
            >
              {grid.map((row, y) => row.map((cell, x) => {
                const isPlayer = playerPos.x === x && playerPos.y === y;
                const isVisible = cell.visible || (cell.item === ItemType.Exit && revealedExitLevel === level);
                const isSeen = cell.seen;
                const isExit = cell.item === ItemType.Exit;
                const wallColor = THEME_WALL_COLOR[theme];

                if (!cell.valid) return <div key={`${x}-${y}`} className="bg-slate-950" />;

                return (
                  <div
                    key={`${x}-${y}`}
                    className={`
                      relative transition-all duration-300
                      ${isVisible ? (backgroundImage ? 'bg-slate-900/40 backdrop-blur-[1px]' : THEME_COLORS[theme]) : isSeen ? 'bg-slate-900/90' : 'bg-black'}
                    `}
                    style={{ boxShadow: isVisible ? 'inset 0 0 6px rgba(0,0,0,0.45)' : undefined }}>
                    {/* Visible floor depth overlay */}
                    {isVisible && !backgroundImage && (
                      <div className="absolute inset-0 bg-black/10 rounded-[1px]" />
                    )}
                    {/* Exit glow pulse */}
                    {isVisible && isExit && (
                      <div className="absolute inset-0 animate-pulse bg-yellow-400/30 rounded-sm z-0" />
                    )}
                    {/* Walls — visible, thick + themed */}
                    {isVisible && (
                      <>
                        {(cell.walls & Direction.N) ? <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                        {(cell.walls & Direction.S) ? <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                        {(cell.walls & Direction.W) ? <div className="absolute top-0 bottom-0 left-0 w-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                        {(cell.walls & Direction.E) ? <div className="absolute top-0 bottom-0 right-0 w-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                      </>
                    )}
                    {/* Items */}
                    {isVisible && cell.item !== ItemType.Empty && ITEMS_CONFIG[cell.item] && (
                      <div className={`absolute inset-0 flex items-center justify-center text-xl drop-shadow-md z-10 ${ITEMS_CONFIG[cell.item].color}`}>
                        {ITEMS_CONFIG[cell.item].char}
                      </div>
                    )}
                    {/* Player */}
                    {isPlayer && (
                      <div className="absolute inset-0 flex items-center justify-center z-20">
                        <PlayerAvatar type={playerIcon} className="w-[85%] h-[85%] drop-shadow-lg animate-bounce-player" />
                      </div>
                    )}
                    {/* Fog Overlay (Seen but not visible) */}
                    {isSeen && !isVisible && (
                      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-[1px]" />
                    )}
                    {/* Walls — explored/fogged, dimmed */}
                    {isSeen && !isVisible && (
                      <>
                        {(cell.walls & Direction.N) ? <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                        {(cell.walls & Direction.S) ? <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                        {(cell.walls & Direction.W) ? <div className="absolute top-0 bottom-0 left-0 w-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                        {(cell.walls & Direction.E) ? <div className="absolute top-0 bottom-0 right-0 w-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                      </>
                    )}
                  </div>
                );
              }))}
            </div>
          </div>

            {/* PICKUP TOAST - floats above controls, doesn't push layout */}
            {pickupToast && (
              <div className="absolute bottom-[185px] left-2 right-2 z-20 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-center gap-3 bg-slate-900/95 border border-slate-600 rounded-xl px-3 py-2 shadow-xl">
                  <span className="text-3xl flex-shrink-0">{pickupToast.char}</span>
                  <div className="min-w-0">
                    <p className={`font-bold text-sm leading-tight ${pickupToast.nameColor}`}>{pickupToast.name}</p>
                    <p className={`text-xs leading-snug mt-0.5 ${pickupToast.usageColor}`}>{pickupToast.usage}</p>
                  </div>
                  <button onClick={() => setPickupToast(null)} className="ml-auto text-slate-500 hover:text-slate-300 flex-shrink-0 text-lg leading-none">✕</button>
                </div>
              </div>
            )}

            {/* CONTROLS BELOW MAZE - Portrait Mode */}
            <div className="flex flex-col gap-2 items-center justify-center flex-shrink-0" style={{paddingBottom:'max(10px, env(safe-area-inset-bottom, 10px))'}}>
              {/* Up Arrow */}
              <button
                onClick={() => handleManualMove(0, -1)}
                className="w-20 h-20 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                aria-label="Move Up"
              >
                ⬆️
              </button>

              {/* Bottom Row: Left, Down, Right */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleManualMove(-1, 0)}
                  className="w-20 h-20 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                  aria-label="Move Left"
                >
                  ⬅️
                </button>

                <button
                  onClick={() => handleManualMove(0, 1)}
                  className="w-20 h-20 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                  aria-label="Move Down"
                >
                  ⬇️
                </button>

                <button
                  onClick={() => handleManualMove(1, 0)}
                  className="w-20 h-20 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                  aria-label="Move Right"
                >
                  ➡️
                </button>
              </div>
            </div>

          </div>

          {/* Landscape Layout: Controls + Maze + Dashboard - ENABLED */}
          <div className="hidden landscape:flex items-center justify-center gap-2 flex-1 min-h-0 px-2">
            {/* LEFT Side in Landscape: Controls OR Dashboard */}
            {landscapeControlsPosition === 'left' ? (
              <div className="flex flex-col gap-2 items-center justify-center flex-shrink-0" style={{paddingLeft:'env(safe-area-inset-left,0px)'}}>
                {/* Position Toggle */}
                <button
                  onClick={() => setLandscapeControlsPosition('right')}
                  className="text-sm bg-slate-800/90 border border-slate-600 rounded px-3 py-1.5 text-white hover:bg-slate-700 mb-1"
                  title="Move controls to right side"
                >
                  ⇄
                </button>
                {/* Controls - NEWS Cardinal Layout */}
                <div className="grid grid-cols-3 gap-1">
                  <div></div>
                  <button onClick={() => handleManualMove(0, -1)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Up">⬆️</button>
                  <div></div>
                  <button onClick={() => handleManualMove(-1, 0)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Left">⬅️</button>
                  <div></div>
                  <button onClick={() => handleManualMove(1, 0)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Right">➡️</button>
                  <div></div>
                  <button onClick={() => handleManualMove(0, 1)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Down">⬇️</button>
                  <div></div>
                </div>
                {/* PICKUP TOAST - below left controls */}
                {pickupToast && (
                  <div className="w-[200px] flex items-center gap-2 bg-slate-900/95 border border-slate-500 rounded-lg px-2 py-1.5 animate-in fade-in duration-300 mt-1">
                    <span className="text-2xl flex-shrink-0">{pickupToast.char}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`font-bold text-xs leading-tight ${pickupToast.nameColor}`}>{pickupToast.name}</p>
                      <p className={`text-[10px] leading-snug mt-0.5 ${pickupToast.usageColor}`}>{pickupToast.usage}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Dashboard on LEFT when controls are on RIGHT */
              <div className="bg-lime-200 border border-lime-300 p-2 rounded flex flex-col gap-1 w-[190px] flex-shrink-0 tall-landscape:self-stretch">
                <div className="flex gap-1">
                  <button onClick={handleOpenLeaderboard} className="flex-1 h-[36px] bg-slate-800/90 hover:bg-slate-700 rounded transition-colors border border-slate-600 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                  </button>
                  <button onClick={() => setShowOperator(true)} className="flex-1 h-[36px] bg-slate-800/90 hover:bg-slate-700 rounded transition-colors border border-slate-600 flex items-center justify-center">
                    <Settings className="w-6 h-6 text-cyan-400" />
                  </button>
                </div>
                <div className="flex gap-1">
                  <div className="flex-1 h-[42px] bg-green-700/90 rounded border border-green-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-white uppercase font-bold leading-none mb-0.5">{t('score', language)}</div>
                    <div className="text-sm font-mono font-bold text-yellow-300 leading-none">{score}</div>
                  </div>
                  <div className="flex-1 h-[42px] bg-slate-800/90 rounded border border-slate-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-white uppercase font-bold leading-none mb-0.5">{t('health', language)}</div>
                    <div className={`text-sm font-mono font-bold ${hp > 50 ? 'text-green-400' : 'text-red-400'} leading-none`}>{hp}%</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <div className="flex-1 h-[32px] bg-indigo-800/90 rounded border border-indigo-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-white uppercase font-bold leading-none mb-0.5">{t('level', language)}</div>
                    <div className="text-sm font-mono font-bold text-cyan-300 leading-none">{level}</div>
                  </div>
                  <div className="flex-1 h-[32px] bg-slate-800/90 rounded border border-slate-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] font-bold text-slate-400 uppercase leading-none">{difficulty.toUpperCase()}</div>
                  </div>
                </div>
                <div className="tall-landscape:flex-1 tall-landscape:flex tall-landscape:flex-col tall-landscape:min-h-0">
                  <div className="text-[10px] font-bold text-slate-400 uppercase text-center leading-none mb-0.5">{t('inventory', language)}</div>
                  <div className="grid grid-cols-4 gap-0.5 tall-landscape:flex-1 tall-landscape:content-start">
                    {Array.from({ length: 16 }).map((_, i) => {
                      const item = bag[i];
                      return (
                        <div key={i} className={`aspect-square tall-landscape:aspect-auto tall-landscape:h-[56px] rounded border ${item ? 'bg-slate-700/90 border-slate-600' : 'bg-slate-300/50 border-slate-400/50'} flex flex-col items-center justify-center overflow-hidden`} title={item ? ITEMS_CONFIG[item]?.name : 'Empty'}>
                          {item && <span className="text-lg leading-none">{ITEMS_CONFIG[item]?.char}</span>}
                          {item && <span className="hidden tall-landscape:block text-[7px] text-slate-300 font-medium leading-none text-center w-full px-0.5 truncate mt-0.5">{ITEMS_CONFIG[item]?.name}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* PICKUP TOAST - inside dashboard panel */}
                {pickupToast && (
                  <div className="mt-1 flex items-center gap-1 bg-slate-900/95 border border-slate-500 rounded-lg px-1.5 py-1 animate-in fade-in duration-300">
                    <span className="text-lg flex-shrink-0">{pickupToast.char}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`font-bold text-[10px] leading-tight ${pickupToast.nameColor}`}>{pickupToast.name}</p>
                    </div>
                    <button onClick={() => setPickupToast(null)} className="text-slate-500 hover:text-slate-300 flex-shrink-0 text-xs leading-none pl-0.5">✕</button>
                  </div>
                )}
              </div>
            )}

            {/* Maze Grid - Landscape CENTER (Maximized Size - takes available space) */}
            <div
              className="relative bg-slate-900/80 backdrop-blur-sm rounded-lg shadow-2xl overflow-hidden border border-slate-800"
              style={{
                width: 'min(calc(100vw - 380px), calc(100vh - 40px))',
                height: 'min(calc(100vw - 380px), calc(100vh - 40px))',
                aspectRatio: '1 / 1',
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
            {/* Grid Rendering */}
            <div 
              className="absolute inset-0 grid"
              style={{
                gridTemplateColumns: `repeat(${grid[0]?.length || 1}, 1fr)`,
                gridTemplateRows: `repeat(${grid.length || 1}, 1fr)`,
              }}
            >
              {grid.map((row, y) => row.map((cell, x) => {
                const isPlayer = playerPos.x === x && playerPos.y === y;
                const isVisible = cell.visible || (cell.item === ItemType.Exit && revealedExitLevel === level);
                const isSeen = cell.seen;
                const isExit = cell.item === ItemType.Exit;
                const wallColor = THEME_WALL_COLOR[theme];

                if (!cell.valid) return <div key={`${x}-${y}`} className="bg-slate-950" />;

                return (
                  <div
                    key={`${x}-${y}`}
                    className={`
                      relative transition-all duration-300
                      ${isVisible ? (backgroundImage ? 'bg-slate-900/40 backdrop-blur-[1px]' : THEME_COLORS[theme]) : isSeen ? 'bg-slate-900/90' : 'bg-black'}
                    `}
                    style={{ boxShadow: isVisible ? 'inset 0 0 6px rgba(0,0,0,0.45)' : undefined }}>
                    {/* Visible floor depth overlay */}
                    {isVisible && !backgroundImage && (
                      <div className="absolute inset-0 bg-black/10 rounded-[1px]" />
                    )}
                    {/* Exit glow pulse */}
                    {isVisible && isExit && (
                      <div className="absolute inset-0 animate-pulse bg-yellow-400/30 rounded-sm z-0" />
                    )}
                    {/* Walls — visible, thick + themed */}
                    {isVisible && (
                      <>
                        {(cell.walls & Direction.N) ? <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                        {(cell.walls & Direction.S) ? <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                        {(cell.walls & Direction.W) ? <div className="absolute top-0 bottom-0 left-0 w-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                        {(cell.walls & Direction.E) ? <div className="absolute top-0 bottom-0 right-0 w-[3px]" style={{ background: wallColor, boxShadow: `0 0 4px ${wallColor}` }} /> : null}
                      </>
                    )}
                    {/* Items */}
                    {isVisible && cell.item !== ItemType.Empty && ITEMS_CONFIG[cell.item] && (
                      <div className={`absolute inset-0 flex items-center justify-center text-xl drop-shadow-md z-10 ${ITEMS_CONFIG[cell.item].color}`}>
                        {ITEMS_CONFIG[cell.item].char}
                      </div>
                    )}
                    {/* Player */}
                    {isPlayer && (
                      <div className="absolute inset-0 flex items-center justify-center z-20">
                        <PlayerAvatar type={playerIcon} className="w-[85%] h-[85%] drop-shadow-lg animate-bounce-player" />
                      </div>
                    )}
                    {/* Fog Overlay (Seen but not visible) */}
                    {isSeen && !isVisible && (
                      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-[1px]" />
                    )}
                    {/* Walls — explored/fogged, dimmed */}
                    {isSeen && !isVisible && (
                      <>
                        {(cell.walls & Direction.N) ? <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                        {(cell.walls & Direction.S) ? <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                        {(cell.walls & Direction.W) ? <div className="absolute top-0 bottom-0 left-0 w-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                        {(cell.walls & Direction.E) ? <div className="absolute top-0 bottom-0 right-0 w-[2px]" style={{ background: `${wallColor}55` }} /> : null}
                      </>
                    )}
                  </div>
                );
              }))}
            </div>
          </div>

            {/* RIGHT Side in Landscape: Dashboard OR Controls */}
            {landscapeControlsPosition === 'right' ? (
              <div className="flex flex-col gap-2 items-center justify-center flex-shrink-0" style={{paddingRight:'env(safe-area-inset-right,0px)'}}>
                {/* Position Toggle */}
                <button
                  onClick={() => setLandscapeControlsPosition('left')}
                  className="text-sm bg-slate-800/90 border border-slate-600 rounded px-3 py-1.5 text-white hover:bg-slate-700 mb-1"
                  title="Move controls to left side"
                >
                  ⇄
                </button>
                {/* Controls - NEWS Cardinal Layout */}
                <div className="grid grid-cols-3 gap-1">
                  <div></div>
                  <button onClick={() => handleManualMove(0, -1)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Up">⬆️</button>
                  <div></div>
                  <button onClick={() => handleManualMove(-1, 0)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Left">⬅️</button>
                  <div></div>
                  <button onClick={() => handleManualMove(1, 0)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Right">➡️</button>
                  <div></div>
                  <button onClick={() => handleManualMove(0, 1)} className="w-11 h-11 bg-slate-800/90 border-2 border-slate-600 rounded hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-xl shadow-lg transition-all active:scale-95" aria-label="Move Down">⬇️</button>
                  <div></div>
                </div>
                {/* PICKUP TOAST - below right controls */}
                {pickupToast && (
                  <div className="w-[200px] flex items-center gap-2 bg-slate-900/95 border border-slate-500 rounded-lg px-2 py-1.5 animate-in fade-in duration-300 mt-1">
                    <span className="text-2xl flex-shrink-0">{pickupToast.char}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`font-bold text-xs leading-tight ${pickupToast.nameColor}`}>{pickupToast.name}</p>
                      <p className={`text-[10px] leading-snug mt-0.5 ${pickupToast.usageColor}`}>{pickupToast.usage}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Dashboard on RIGHT when controls are on LEFT */
              <div className="bg-lime-200 border border-lime-300 p-2 rounded flex flex-col gap-1 w-[190px] flex-shrink-0 tall-landscape:self-stretch">
                <div className="flex gap-1">
                  <button onClick={handleOpenLeaderboard} className="flex-1 h-[36px] bg-slate-800/90 hover:bg-slate-700 rounded transition-colors border border-slate-600 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                  </button>
                  <button onClick={() => setShowOperator(true)} className="flex-1 h-[36px] bg-slate-800/90 hover:bg-slate-700 rounded transition-colors border border-slate-600 flex items-center justify-center">
                    <Settings className="w-6 h-6 text-cyan-400" />
                  </button>
                </div>
                <div className="flex gap-1">
                  <div className="flex-1 h-[42px] bg-green-700/90 rounded border border-green-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-white uppercase font-bold leading-none mb-0.5">{t('score', language)}</div>
                    <div className="text-sm font-mono font-bold text-yellow-300 leading-none">{score}</div>
                  </div>
                  <div className="flex-1 h-[42px] bg-slate-800/90 rounded border border-slate-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-white uppercase font-bold leading-none mb-0.5">{t('health', language)}</div>
                    <div className={`text-sm font-mono font-bold ${hp > 50 ? 'text-green-400' : 'text-red-400'} leading-none`}>{hp}%</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <div className="flex-1 h-[32px] bg-indigo-800/90 rounded border border-indigo-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-white uppercase font-bold leading-none mb-0.5">{t('level', language)}</div>
                    <div className="text-sm font-mono font-bold text-cyan-300 leading-none">{level}</div>
                  </div>
                  <div className="flex-1 h-[32px] bg-slate-800/90 rounded border border-slate-600 flex flex-col items-center justify-center">
                    <div className="text-[10px] font-bold text-slate-400 uppercase leading-none">{difficulty.toUpperCase()}</div>
                  </div>
                </div>
                <div className="tall-landscape:flex-1 tall-landscape:flex tall-landscape:flex-col tall-landscape:min-h-0">
                  <div className="text-[10px] font-bold text-slate-400 uppercase text-center leading-none mb-0.5">{t('inventory', language)}</div>
                  <div className="grid grid-cols-4 gap-0.5 tall-landscape:flex-1 tall-landscape:content-start">
                    {Array.from({ length: 16 }).map((_, i) => {
                      const item = bag[i];
                      return (
                        <div key={i} className={`aspect-square tall-landscape:aspect-auto tall-landscape:h-[56px] rounded border ${item ? 'bg-slate-700/90 border-slate-600' : 'bg-slate-300/50 border-slate-400/50'} flex flex-col items-center justify-center overflow-hidden`} title={item ? ITEMS_CONFIG[item]?.name : 'Empty'}>
                          {item && <span className="text-lg leading-none">{ITEMS_CONFIG[item]?.char}</span>}
                          {item && <span className="hidden tall-landscape:block text-[7px] text-slate-300 font-medium leading-none text-center w-full px-0.5 truncate mt-0.5">{ITEMS_CONFIG[item]?.name}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* PICKUP TOAST - inside dashboard panel */}
                {pickupToast && (
                  <div className="mt-1 flex items-center gap-1 bg-slate-900/95 border border-slate-500 rounded-lg px-1.5 py-1 animate-in fade-in duration-300">
                    <span className="text-lg flex-shrink-0">{pickupToast.char}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`font-bold text-[10px] leading-tight ${pickupToast.nameColor}`}>{pickupToast.name}</p>
                    </div>
                    <button onClick={() => setPickupToast(null)} className="text-slate-500 hover:text-slate-300 flex-shrink-0 text-xs leading-none pl-0.5">✕</button>
                  </div>
                )}
              </div>
            )}
          </div> {/* End Landscape Layout */}

          {/* On-Screen Controls - Portrait Mode (Enlarged) - DISABLED for side layout */}
          <div className="hidden" style={{display: 'none'}}>
            {/* Top Row: Up Arrow */}
            <div className="flex justify-center">
              <button
                onClick={() => handleManualMove(0, -1)}
                className="w-14 h-14 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                aria-label="Move Up"
              >
                ⬆️
              </button>
            </div>

            {/* Bottom Row: Left, Down, Right */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => handleManualMove(-1, 0)}
                className="w-14 h-14 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                aria-label="Move Left"
              >
                ⬅️
              </button>

              <button
                onClick={() => handleManualMove(0, 1)}
                className="w-14 h-14 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                aria-label="Move Down"
              >
                ⬇️
              </button>

              <button
                onClick={() => handleManualMove(1, 0)}
                className="w-14 h-14 bg-slate-800/90 border-2 border-slate-600 rounded-xl hover:bg-slate-700 active:bg-slate-600 flex items-center justify-center text-3xl shadow-lg transition-all active:scale-95"
                aria-label="Move Right"
              >
                ➡️
              </button>
            </div>
          </div>

          {/* AD SPACE BANNER - Below Controls (REMOVED - Merged into scrolling banner) */}
          {/* 
          {!showLauncher && (
            <div className="w-full h-[40px] bg-white border-t-2 border-gray-300 flex items-center justify-center px-4 flex-shrink-0">
              <span className="text-sm font-bold text-black text-center uppercase tracking-wide">Advertisement Space</span>
            </div>
          )}
          */}
        </div> {/* end maze panel wrapper */}

        {/* ENCOUNTER OVERLAY */}
        {modal.isOpen && (modal.type === 'question' || modal.type === 'inventory') && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-start landscape:justify-center overflow-y-auto bg-black/90 backdrop-blur-md p-4 gap-2">
            <div className="w-full max-w-md my-auto">
              {renderEncounter()}
            </div>
            {/* Hint button visible during encounters */}
            <button
              onClick={() => setShowHintPanel(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/40 border border-amber-500/50 rounded-full text-amber-300 text-xs font-semibold transition-colors"
            >
              💡 Item Guide
            </button>
          </div>
        )}

        {/* HINT PANEL */}
        {showHintPanel && (
          <div className="absolute inset-0 z-50 bg-black/80 flex items-center justify-center p-3" onClick={() => setShowHintPanel(false)}>
            <div className="bg-slate-900 border border-amber-500/60 rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💡</span>
                  <div>
                    <h2 className="text-base font-bold text-amber-300">Item Hint Guide</h2>
                    <p className="text-xs text-slate-400">Use the right item to survive each hazard</p>
                  </div>
                </div>
                <button onClick={() => setShowHintPanel(false)} className="text-slate-400 hover:text-white text-xl leading-none">✕</button>
              </div>
              <div className="p-3 space-y-1.5">
                {(Object.entries(ITEM_USAGE) as [ItemTypeValues, { usage: string; usageColor: string }][]).map(([itemKey, info]) => {
                  const cfg = ITEMS_CONFIG[itemKey];
                  if (!cfg) return null;
                  return (
                    <div key={itemKey} className="flex items-start gap-3 bg-slate-800/60 rounded-lg px-3 py-2 border border-slate-700/50">
                      <span className="text-2xl flex-shrink-0 mt-0.5">{cfg.char}</span>
                      <div className="min-w-0">
                        <p className={`font-semibold text-sm leading-tight ${cfg.color}`}>{cfg.name}</p>
                        <p className={`text-xs leading-snug mt-0.5 ${info.usageColor}`}>{info.usage}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* MODALS */}
        {showLeaderboard && <Leaderboard scores={getHighScores()} onClose={() => setShowLeaderboard(false)} />}
        {showOperator && (
          <OperatorPanel 
            onClose={() => setShowOperator(false)} 
            musicVolume={musicVolume}
            setMusicVolume={setMusicVolume}
            sfxVolume={sfxVolume}
            setSfxVolume={setSfxVolume}
          />
        )}
        {showSignup && <SignupPrompt onSignup={handleSignup} onSkip={() => { setShowSignup(false); }} />}
        {showPaywall && <Paywall onUnlock={handlePaywallUnlock} onClose={() => { setShowPaywall(false); setPendingLevel(null); setShowLauncher(true); }} />}
        
        <MusicPlayer 
          src="https://upload.wikimedia.org/wikipedia/commons/transcoded/2/20/Silent_Partner_-_Bet_On_It.ogg/Silent_Partner_-_Bet_On_It.ogg.mp3" 
          volume={musicVolume} 
        />

{/* MICRO ADVERTISEMENT BANNER — Android Portrait Base (Merged & Scrolling) */}
{!showLauncher && !Capacitor.isNativePlatform() && <HouseAd variant="ticker" placement="mobile_gameplay_footer" className="landscape:hidden" />}


        {/* Mobile Modal Container */}
        {modal.isOpen && modal.type !== 'inventory' && modal.type !== 'question' && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div
              className="rounded-xl w-full max-w-sm shadow-2xl p-6 animate-in zoom-in max-h-[90vh] overflow-y-auto"
              style={modal.type === 'levelComplete' ? {
                background: 'linear-gradient(135deg, #0f172a 60%, #1e1b4b 100%)',
                border: '2px solid transparent',
                backgroundClip: 'padding-box',
                animation: 'rainbowBorder 1s linear infinite, zoomIn 0.3s ease',
                boxShadow: '0 0 40px rgba(251,191,36,0.4), 0 0 80px rgba(168,85,247,0.2)',
              } : {
                background: '#0f172a',
                border: '1px solid #334155',
              }}
            >
              {modal.type === 'levelComplete' && (
                <div className="text-center text-4xl mb-2 animate-bounce">🎉🏆🎉</div>
              )}
              <h2 className="text-xl font-bold mb-2" style={modal.type === 'levelComplete' ? {
                background: 'linear-gradient(90deg, #f59e0b, #a78bfa, #34d399, #f59e0b)',
                backgroundSize: '200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shimmerText 1.5s linear infinite',
              } : { color: 'white' }}>{modal.title}</h2>
              <p className="text-slate-300 mb-4">{modal.message}</p>
              <button
                onClick={() => { playSound('bleep'); modal.onConfirm && modal.onConfirm(); }}
                className="w-full py-3 text-white rounded-lg font-bold transition hover:scale-105"
                style={modal.type === 'levelComplete' ? {
                  background: 'linear-gradient(90deg, #7c3aed, #2563eb, #059669)',
                  backgroundSize: '200%',
                  animation: 'shimmerText 1.2s linear infinite',
                } : { background: '#0891b2' }}
              >
                {modal.type === 'gameOver' ? "Don't ever Quit! Play Again" : "Continue"}
              </button>
              {/* Share Buttons for Level Complete and Game Over */}
              {(modal.type === 'levelComplete' || modal.type === 'gameOver') && (
                <ShareButtons score={score} level={level} isWin={modal.type === 'levelComplete'} />
              )}
            </div>
            <style>{`
              @keyframes rainbowBorder {
                0%   { box-shadow: 0 0 30px #f59e0b88, 0 0 60px #f59e0b44; border-color: #f59e0b; }
                25%  { box-shadow: 0 0 30px #a78bfa88, 0 0 60px #a78bfa44; border-color: #a78bfa; }
                50%  { box-shadow: 0 0 30px #34d39988, 0 0 60px #34d39944; border-color: #34d399; }
                75%  { box-shadow: 0 0 30px #f472b688, 0 0 60px #f472b644; border-color: #f472b6; }
                100% { box-shadow: 0 0 30px #f59e0b88, 0 0 60px #f59e0b44; border-color: #f59e0b; }
              }
              @keyframes shimmerText {
                0%   { background-position: 0% 50%; }
                100% { background-position: 200% 50%; }
              }
            `}</style>
          </div>
        )}
      </div>

        {/* DESKTOP RIGHT AD */}
        <div className="hidden landscape:flex w-[80px] bg-slate-900 border-l border-slate-800 flex-col items-center justify-center p-1 gap-2">
           <HouseAd variant="rail" placement="desktop_gameplay_rail" />
        </div>

      </div> {/* End MAIN CONTENT WRAPPER */}
    </div>

    {/* Confetti overlay — appears on level complete */}
    {showConfetti && (
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 70, overflow: 'hidden' }}>
        {Array.from({ length: 48 }).map((_, i) => {
          const colors = ['#f59e0b','#a78bfa','#34d399','#f472b6','#60a5fa','#fb923c','#4ade80','#e879f9','#facc15','#38bdf8'];
          const color = colors[i % colors.length];
          const left = `${((i * 2.08 + Math.sin(i * 0.9) * 15 + 2) % 98) + 1}%`;
          const delay = `${(i * 0.06) % 1.8}s`;
          const dur = `${1.4 + (i % 5) * 0.25}s`;
          const size = `${5 + (i % 5) * 3}px`;
          const shape = i % 3 === 0 ? '50%' : i % 3 === 1 ? '2px' : '0';
          return (
            <div key={i} style={{
              position: 'absolute',
              left,
              top: '-24px',
              width: size,
              height: i % 4 === 0 ? `${parseInt(size) * 2}px` : size,
              background: color,
              borderRadius: shape,
              opacity: 0.9,
              animation: `confettiFall ${dur} ${delay} ease-in forwards`,
            }} />
          );
        })}
        <style>{`
          @keyframes confettiFall {
            0%   { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
            80%  { opacity: 0.8; }
            100% { transform: translateY(105vh) rotate(840deg) scale(0.6); opacity: 0; }
          }
        `}</style>
      </div>
    )}
    </>
  );
};
